import type { AppLocale } from "./config";
import { EXTENDED_EN, EXTENDED_ES, EXTENDED_FR } from "./app-i18n-extended";

/** Flat dot-keys for type-safe-ish lookups */
export type MessageDict = Record<string, string>;

const en: MessageDict = {
  "nav.dashboard": "Dashboard",
  "nav.calendar": "Calendar",
  "nav.quotes": "Quotes",
  "nav.newQuote": "New quote",
  "nav.customers": "Customers",
  "nav.templates": "Templates",
  "nav.settings": "Settings",
  "nav.home": "Home",
  "nav.new": "New",
  "nav.clients": "Customers",
  "nav.more": "More",
  "nav.billing": "Billing",
  "nav.help": "Help",
  "nav.primary": "Primary",
  "billingGate.subtitle": "Start a plan before creating your first quote.",
  "billingGate.cardTitle": "Billing required",
  "billingGate.cardDesc":
    "Your free trial has ended. Add a plan to keep creating and sending quotes. Your existing quotes and customers are safe.",
  "billingGate.browseNote":
    "You can still browse the app, but creating quotes requires an active plan after the trial.",
  "billingGate.choosePlan": "Choose a plan",
  "billingGate.backToDashboard": "Back to dashboard",
  "billingBanner.platformTrial.one":
    "1 day left on your free trial. Pick a plan and add your card before it ends to keep full access.",
  "billingBanner.platformTrial.many":
    "{days} days left on your free trial. Pick a plan and add your card before it ends to keep full access.",
  "billingBanner.pickPlan": "Pick a plan",
  "billingBanner.stripeTrialing.one":
    "1 day left on your intro trial. Add a payment method before it ends to keep full access.",
  "billingBanner.stripeTrialing.many":
    "{days} days left on your intro trial. Add a payment method before it ends to keep full access.",
  "billingBanner.addPayment": "Add payment method",
  "billingBanner.billing": "Billing",
  "billingBanner.ownerPromo.one":
    "About 1 day left on your free promotional plan. Add a payment method under Billing before it ends to keep full access.",
  "billingBanner.ownerPromo.many":
    "About {days} days left on your free promotional plan. Add a payment method under Billing before it ends to keep full access.",
  "billingBanner.promoPick":
    "Your promotional access ended. Choose a paid plan to keep using Jorvio. We recommend {tier} (the tier you had on the promo).",
  "billingBanner.choosePlan": "Choose plan",
  "billingBanner.trialEnded":
    "Your free trial ended. Your workspace is view-only. Pick a plan and add your card to start creating, sending, and saving again.",
  "billingBanner.paymentFailed":
    "We couldn't process your last payment. Your workspace is view-only until the card is updated. You can still browse quotes and jobs.",
  "billingBanner.updatePayment": "Update payment",
  "a11y.skipToMain": "Skip to main content",
  "nav.followUpsOpenQuotes": "Follow-ups & analytics",
  "nav.followUpsShort": "Follow-ups",
  "nav.messages": "Messages",
  "nav.jobs": "Jobs",
  "jobs.title": "Jobs",
  "jobs.subtitle":
    "Every job in one list, with its linked quotes, schedule, and remaining balance.",
  "jobs.unscheduled": "Not scheduled",
  "jobs.noCustomerLinked": "No customer linked",
  "jobs.tab.active": "Active",
  "jobs.tab.completed": "Completed",
  "jobs.tab.archived": "Archived",
  "jobs.emptyTitle": "No jobs yet",
  "jobs.emptyDesc": "Create a manual job or accept a quote to generate one automatically.",
  "jobs.newJob": "New job",
  "jobs.backDashboard": "Back to dashboard",
  "jobs.backToList": "All jobs",
  "jobs.loadErrorTitle": "Could not load jobs",
  "jobs.loadErrorDesc": "Try again in a moment.",
  "jobs.scheduled": "Scheduled",
  "jobs.quotesLinked": "Quotes",
  "jobs.completedLabel": "Completed",
  "jobs.openDetail": "Open →",
  "jobs.fieldTitle": "Job title",
  "jobs.fieldTitlePlaceholder": "e.g. Smith kitchen remodel",
  "jobs.fieldStartOptional": "Start (optional)",
  "jobs.fieldEndOptional": "End (optional)",
  "jobs.fieldStartDate": "Start date",
  "jobs.fieldEndDate": "End date",
  "jobs.fieldTimeOptional": "Time",
  "jobs.schedulingSectionTitle": "Scheduling (optional)",
  "jobs.scheduleDefaultsHint":
    "Choose a date for start and/or end. Leave each time blank to use 9:00 AM for start and 5:00 PM for end in your local time—nothing is filled in until you pick a time yourself.",
  "jobs.fieldSite": "Job site address",
  "jobs.fieldNotes": "Notes",
  "jobs.cancel": "Cancel",
  "jobs.create": "Create",
  "jobs.saving": "Saving…",
  "jobs.createNeedTitle": "Enter a job title.",
  "jobs.createFailed": "Could not create job",
  "jobs.createOk": "Job created",
  "jobs.payment.paid": "Paid",
  "jobs.payment.partial": "Partially paid",
  "jobs.payment.unpaid": "Unpaid",
  "jobs.status.completed": "Completed",
  "jobs.status.scheduled": "Scheduled",
  "jobs.status.notScheduled": "Not scheduled",
  "jobs.photos.sectionTitle": "Before & after photos",
  "jobs.photos.before": "Before",
  "jobs.photos.after": "After",
  "jobs.photos.none": "No photos yet",
  "jobs.photos.hint": "Optional. Customers can view these on their portal.",
  "jobs.photos.beforeLabel": "Before photos",
  "jobs.photos.afterLabel": "After photos",
  "jobs.photos.addBefore": "Add before",
  "jobs.photos.addAfter": "Add after",
  "jobs.photos.remove": "Remove",
  "jobs.photos.uploading": "Uploading…",
  "jobs.photos.markComplete": "Mark complete",
  "jobs.photos.markCompleteNoAfterTitle": "Mark complete without after photos?",
  "jobs.photos.markCompleteNoAfterBody":
    "You have not added any after photos. Customers often appreciate seeing the finished work. You can still mark the job complete.",
  "jobs.photos.markCompleteConfirm": "Mark complete anyway",
  "jobs.detail.loadError": "Could not load job",
  "jobs.detail.saveFailed": "Could not save",
  "jobs.detail.readOnlyTitle": "View-only workspace",
  "jobs.detail.readOnlyBody": "Add a plan or fix billing in Settings → Billing to edit jobs again.",
  "jobs.detail.saved": "Saved",
  "jobs.detail.actionFailed": "Could not update",
  "jobs.detail.attachFailed": "Could not link quote",
  "jobs.detail.attached": "Quote linked",
  "jobs.detail.detached": "Quote unlinked",
  "jobs.detail.deleteConfirm": "Delete this job permanently? Calendar links are cleared.",
  "jobs.detail.deleteFailed": "Could not delete",
  "jobs.detail.deleted": "Job deleted",
  "jobs.detail.notFound": "Job not found.",
  "jobs.detail.edit": "Edit details",
  "jobs.detail.markIncomplete": "Mark not complete",
  "jobs.detail.markComplete": "Mark complete",
  "jobs.detail.unarchive": "Restore",
  "jobs.detail.archive": "Archive",
  "jobs.detail.delete": "Delete",
  "jobs.detail.noSite": "No site address",
  "jobs.detail.paymentTitle": "Payments (linked quotes)",
  "jobs.detail.percentPaid": "{{pct}}% paid",
  "jobs.detail.ofDue": "of",
  "jobs.detail.scheduleTitle": "Schedule & notes",
  "jobs.detail.scheduleHint": "Shown on your calendar when dates are set.",
  "jobs.detail.scheduleDayPickerTitle": "Job schedule",
  "jobs.detail.scheduleDayPickerHint":
    "Click a day to add or remove it. Selected days sync to your company calendar (9 AM–5 PM local). Changes save automatically.",
  "jobs.detail.scheduleDayPickerSaving": "Saving…",
  "jobs.detail.scheduleRangeTitle": "Date range & times",
  "jobs.detail.scheduleStartDate": "Start date",
  "jobs.detail.scheduleEndDate": "End date",
  "jobs.detail.scheduleStartTime": "Start time",
  "jobs.detail.scheduleEndTime": "End time",
  "jobs.detail.scheduleRepeatDays": "Repeat on days",
  "jobs.detail.excludeWeekends": "Exclude weekends",
  "jobs.detail.weekendDisabledHint": "Turn off “Exclude weekends” to schedule Saturday or Sunday.",
  "jobs.detail.scheduleRepeatHint":
    "Leave all weekday buttons off to count every calendar day from the start date through the end date. Turn specific weekdays on only when work should repeat on those days inside the range (for example Tue/Thu only). Exclude weekends means Monday–Friday only.",
  "jobs.detail.configureSchedule": "Configure schedule",
  "jobs.detail.configureScheduleDialogDesc":
    "Add the people on this job and set each one's work days. Click days on the calendar to turn them on or off. This is internal only \u2014 it never changes anything the customer sees.",
  "jobs.detail.scheduleCardTeaser":
    "Use Configure schedule to add team members and set each person's work days. Internal only \u2014 customers don't see this. Notes stay on this page.",
  "jobs.detail.scheduleCalendarTitle": "Work calendar",
  "jobs.detail.scheduleCalendarNeedDates":
    "Set a start date first (and an end date if the job spans more than one day). Then you can turn individual days on or off in the grid.",
  "jobs.detail.schedulePrevMonth": "Previous month",
  "jobs.detail.scheduleNextMonth": "Next month",
  "jobs.detail.scheduleSaveAndClose": "Save and close",
  "jobs.detail.clearSchedule": "Clear schedule",
  "jobs.detail.notesPlaceholder": "Access, parking, materials, customer preferences. Visible to your team on this job.",
  "jobs.detail.clickToAddSite": "Click to add a site address",
  "jobs.detail.editDialogHint":
    "Use Schedule & notes on this page for the work calendar. Here you can update the title, site address, and notes.",
  "jobs.detail.starts": "Starts",
  "jobs.detail.ends": "Ends",
  "jobs.detail.noStart": "No start time — edit details to add dates.",
  "jobs.detail.invalidScheduleStart": "That start date or time isn’t valid. Use a proper date, or leave time blank for a 9:00 AM start.",
  "jobs.detail.invalidScheduleEnd": "That end date or time isn’t valid. Use a proper date, or leave time blank for a 5:00 PM end.",
  "jobs.detail.notesLabel": "Notes",
  "jobs.detail.quotesTitle": "Linked quotes",
  "jobs.detail.attachQuote": "Link quote",
  "jobs.detail.noQuotesToAttach": "No accepted quotes available to link.",
  "jobs.detail.pickQuote": "Quote",
  "jobs.detail.pickPlaceholder": "Choose a quote",
  "jobs.detail.link": "Link",
  "jobs.detail.noQuotes": "No quotes linked yet.",
  "jobs.detail.primary": "Primary",
  "jobs.detail.makePrimary": "Make primary",
  "jobs.detail.detach": "Unlink",
  "jobs.detail.save": "Save",
  "jobs.detail.paymentEdit": "Manually edit payment amounts",
  "jobs.detail.paymentEditShort": "Edit amounts",
  "jobs.detail.paymentEditDesc":
    "Set amount paid and, if needed, a custom amount due per linked quote. Leave amount due blank to use the total from the quote.",
  "jobs.detail.paymentFromQuote": "From quote:",
  "jobs.detail.paymentAmountDue": "Amount due",
  "jobs.detail.paymentAmountDueHint": "Leave blank to use the quote total. Enter a number to override.",
  "jobs.detail.paymentAmountPaid": "Amount paid",
  "jobs.detail.paymentStripeNote":
    "Card payments through Stripe still update paid totals automatically; you can adjust here for cash, checks, or corrections.",
  "jobs.detail.paymentStripeFeeHint":
    "Totals here are your quote amounts (what you earn). When card processing fees are passed to customers, their pay link may show slightly higher numbers — that difference is the Stripe fee, not missing revenue.",
  "jobs.detail.paymentStripePayoutHint":
    "Card payments update the paid total here right away, but Stripe deposits funds to your bank on its standard schedule — usually about 2 business days after each payment. Your first payout can take 7–14 days while Stripe verifies your account.",
  "jobs.detail.paymentStripePolicyLink": "Stripe payout policy",
  "jobs.detail.paymentHideStripeInfo": "Hide",
  "jobs.detail.paymentShowStripeInfo": "Show payment info",
  "jobs.detail.paymentInvalidPaid": "Enter a valid amount paid (0 or more).",
  "jobs.detail.paymentInvalidDue": "Enter a valid amount due or leave it blank.",
  "jobs.detail.paymentSaveFailed": "Could not save payment amounts",
  "jobs.detail.paymentSaved": "Payment amounts saved",
  "jobs.detail.requestBalancePayment": "Request remaining payment",
  "jobs.detail.balanceReminderOpening": "Loading…",
  "jobs.detail.balanceReminderTitle": "Email customer — balance due",
  "jobs.detail.balanceReminderDesc":
    "Send a reminder with the same secure pay link from the original estimate.",
  "jobs.detail.balanceReminderEmailHelp":
    "If anything about the timing or amount is unclear, open your estimate link and message your contractor there.",
  "jobs.detail.balanceReminderSubjectLabel": "Subject",
  "jobs.detail.balanceReminderMessageLabel": "Message",
  "jobs.detail.balanceReminderSubjectPrefix": "Balance due",
  "jobs.detail.balanceReminderSend": "Send email",
  "jobs.detail.balanceReminderSending": "Sending…",
  "jobs.detail.balanceReminderSent": "Reminder sent",
  "jobs.detail.balanceReminderSentDesc": "The customer will receive your message by email.",
  "jobs.detail.balanceReminderSendFailed": "Could not send reminder",
  "jobs.detail.balanceReminderOpenFailed": "Could not open reminder",
  "jobs.detail.balanceReminderUnavailable": "Balance reminder unavailable",
  "jobs.detail.balanceReminderUnavailableDesc":
    "This quote has no remaining online balance to collect, or split payments are not set up.",
  "jobs.detail.balanceReminderNoShareLink": "Send the quote to the customer first so they have a pay link.",
  "jobs.detail.balanceReminderNoEmail": "Add a customer email on the linked quote before sending a reminder.",
  "jobs.detail.balanceReminderTooShort": "Message too short",
  "jobs.detail.balanceReminderTooShortDesc": "Please add a bit more detail before sending.",
  "jobs.detail.paymentShowBreakdown": "Show breakdown",
  "jobs.detail.paymentHideBreakdown": "Hide breakdown",
  "jobs.detail.paymentBreakdownTitle": "By quote",
  "jobs.detail.paymentSplitHint": "{{pct}}% deposit · balance due later",
  "jobs.detail.paymentDueAtEndHint": "Full amount due when work is complete",
  "jobs.detail.requestDepositPayment": "Request deposit",
  "jobs.detail.requestFullPayment": "Request payment",
  "jobs.detail.depositReminderSubjectPrefix": "Deposit due",
  "jobs.detail.paymentReminderSubjectPrefix": "Payment due",
  "jobs.detail.depositReminderTitle": "Email customer — deposit due",
  "jobs.detail.paymentReminderTitle": "Email customer — payment due",
  "jobs.detail.openJob": "Open job",
  "projects.detail.paymentTitle": "Payments (all jobs in project)",
  "settings.title": "Settings",
  "settings.subtitle": "Company profile and what customers see on quotes.",
  "settings.language": "Language",
  "settings.languageDescription": "Menus, buttons, and AI-written quote text (title, scope, lines).",
  "settings.saveHint": "Changes are saved only when you click Save.",
  "settings.saved": "Saved",
  "settings.save": "Save",
  "settings.profileUpdated": "Your profile was updated.",
  "settings.saveProfile": "Save profile",
  "settings.saveProfileHint":
    "Saves language, contact info, and branding. Language updates labels across the app.",
  "settings.saving": "Saving…",
  "settings.saveChanges": "Save all settings",
  "settings.unsavedChanges": "You have unsaved profile changes.",
  "settings.offlineBanner": "You are offline. Profile changes are stored on this device and sync when you reconnect.",
  "settings.autosaveHint": "Profile changes save automatically.",
  "settings.offlineSavedLocal": "Saved on this device. They will sync to your account when you are back online.",
  "settings.offlineQueuedNetwork": "Could not reach the server. Your changes are stored on this device and will retry when the connection improves.",
  "settings.onlineSynced": "Offline changes were synced to your account.",
  "settings.offlineLocalStorageFailed": "Could not store a local copy of your changes. Check browser storage permissions or free space.",
  "settings.leaveUnsaved":
    "You have unsaved profile changes. Leave this page without saving?",
  "settings.partialSaveMigrationHint":
    "Other fields saved. Personal phone and quote contact email couldn't be saved. If this keeps happening, contact support.",
  "signup.title": "Create your account",
  "signup.language": "Language",
  "signup.languageHint": "Used for the app and AI-written quotes. Change anytime in Settings.",
  "signup.description": "Create your account with a password, then confirm from your email to finish.",
  "signup.requiredBefore": "Fields marked with",
  "signup.requiredAfter": "are required.",
  "signup.fieldOptional": "(optional)",
  "signup.email": "Email",
  "signup.firstName": "First name",
  "signup.lastName": "Last name",
  "signup.companyName": "Company name",
  "signup.trade": "Trade",
  "signup.selectTrade": "Select trade",
  "signup.tradeCustom": "Your trade or specialty",
  "signup.city": "City",
  "signup.state": "State",
  "signup.password": "Password",
  "signup.confirmPassword": "Confirm password",
  "signup.createAccount": "Create account",
  "signup.creatingAccount": "Creating account…",
  "signup.alreadyHaveAccount": "Already have an account?",
  "signup.signIn": "Sign in",
  "signup.consentBefore": "I agree to the",
  "signup.termsOfService": "Terms of Service",
  "signup.privacyPolicy": "Privacy Policy",
  "signup.trades.hvac": "HVAC",
  "signup.trades.plumbing": "Plumbing",
  "signup.trades.electrical": "Electrical",
  "signup.trades.roofing": "Roofing",
  "signup.trades.landscaping": "Landscaping",
  "signup.trades.painting": "Painting",
  "signup.trades.general": "General contractor",
  "signup.trades.other": "Other",
  "public.language": "Language",
  "public.languageSaved": "Language preference saved.",
  "public.professionalEstimate": "Estimate",
  "public.threadTitle": "Message your contractor",
  "public.threadSubtitle":
    "Same thread as your contractor’s office. Timing, scope, follow-ups.",
  "public.threadEmpty":
    "No messages yet. Ask something or confirm details — they’ll reply here.",
  "public.threadYourMessage": "Your message",
  "public.threadPlaceholder": "e.g. Can we start next Tuesday?",
  "public.threadSend": "Send message",
  "public.threadSending": "Sending…",
  "public.threadYou": "You",
  "public.threadContractor": "Contractor",
  "messages.you": "You (contractor)",
  "messages.customer": "Customer",
  "messages.translatedHint": "Translated for your language",
  "messages.threadTitle": "Messages with customer",
  "messages.threadSubtitle":
    "Same thread the customer sees on the public estimate after send. Messages may be auto-translated below.",
  "messages.noMessages":
    "No messages yet. After you send the quote, your first note shows on their quote page.",
  "messages.reply": "Reply to customer",
  "messages.send": "Send to customer",
  "messages.sending": "Sending…",
  "messages.loading": "Loading…",
  "messages.pageTitle": "Messages",
  "messages.pageSubtitle": "Portal questions from customers and quote message threads for your company.",
  "messages.loadFailedTitle": "Could not load messages",
  "messages.replyTooShort": "Enter a reply first.",
  "messages.replySaved": "Reply saved.",
  "messages.emailNotifyTitle": "Email notifications",
  "messages.emailNotifyDescription":
    "Choose whether we email you whenever you receive a message.",
  "messages.emailNotifyCheckbox":
    "Email me when I receive a new customer message (uses your quote contact address).",
  "messages.newMessage": "New message",
  "messages.groupCustomers": "Customers",
  "messages.groupCustomersHint": "Customers reaching out and replies on sent quotes",
  "messages.groupTeam": "Your team",
  "messages.groupTeamHint": "Crew, subcontractors, and co-operators on your workspace",
  "messages.sectionPortalContact": "Customer portal contact requests",
  "messages.emptyPortalContact": "No portal messages yet.",
  "messages.sectionQuoteThreads": "Quote message threads",
  "messages.emptyQuoteThreads":
    "No quote threads yet. When customers message you from a sent quote, the full thread appears here.",
  "messages.sectionTeamThreads": "Crew & co-operator messages",
  "messages.emptyTeamThreads":
    "No team conversations yet. Start a new message above to ping someone on your crew or your co-operator.",
  "messages.replied": "Replied",
  "messages.needsReply": "Needs reply",
  "messages.waitingOnCustomer": "Waiting on customer",
  "messages.yourReply": "Your reply",
  "messages.sendReply": "Send reply",
  "messages.untitledQuote": "Quote",
  "messages.openQuote": "Open quote",
  "messages.messageCount": "messages",
  "messages.customerLink": "Customer-facing link",
  "messages.youContractor": "You",
  "messages.emailNotifyTapHint": "Click to change",
  "messages.emailNotifyCloseHint": "Click to collapse",
  "messages.portalThreadSubtitle": "Customer portal",
  "messages.composerPlaceholder": "Message…",
  "messages.composerHint": "Enter to send · Shift+Enter for a new line",
  "messages.quoteThreadFooter": "Send new messages from the quote page; this thread stays in sync.",
  "messages.tabAll": "All",
  "messages.tabPortal": "Portal",
  "messages.tabQuotes": "Quotes",
  "messages.tabTeam": "Team",
  "messages.sourcePortal": "Portal",
  "messages.sourceQuote": "Quote",
  "messages.sourceTeam": "Team",
  "messages.selectConversation": "Select a conversation",
  "messages.selectConversationHint": "Choose a thread on the left to read and reply.",
  "messages.emptyInbox": "No conversations in this tab yet.",
  "messages.backToList": "All messages",

  "common.error": "Error",
  "common.copied": "Copied",

  "settings.stripe.notEnabled":
    "Stripe Connect isn’t on for this account yet. Enable Connect in Stripe, then link your payout account again.",
  "settings.stripe.migration":
    "We couldn't save your payout settings. Try again, or contact support if it keeps failing.",
  "settings.stripe.unknown": "Something went wrong while starting payout setup. Please try again.",
  "settings.stripe.success":
    "Stripe payout setup was opened successfully. If you finished onboarding there, your payout account should be ready once Stripe completes verification.",

  "settings.plan.title": "Plan & billing",
  "settings.plan.currentPlan": "Current plan",
  "settings.plan.planStatus": "Plan status",
  "settings.plan.noPlan": "No plan",
  "settings.plan.sentQuotesUsed": "{{used}} of {{limit}} sent quotes this month.",
  "settings.plan.sentQuotesUsedUnlimited": "{{used}} sent quotes this month — unlimited on every plan.",
  "settings.plan.monthlySentQuotesUsage": "Sent quotes this month",
  "settings.plan.sentQuotesDraftNote":
    "Every plan includes unlimited sent quotes — email, PDF export, and copy link all share the same workflow. Drafts and preview never count.",
  "settings.plan.startPlan": "Choose a plan to create quotes.",
  "settings.plan.trialPrices":
    "Every plan starts with a 7-day free trial — no card required. After day 7, add a card and pick a plan to keep creating quotes.",
  "settings.plan.compareIntro": "Compare features, AI tools, and team access. Every plan includes unlimited sent quotes.",
  "settings.plan.ownerPromoTrialCallout":
    "Promo code trial — about {{days}} calendar day(s) left on your gifted plan. Add a card under Billing before it ends to keep access.",
  "settings.plan.trialPanelLabel": "Trials & intro pricing",
  "settings.plan.aiFeaturesHasPlan": "Compare plans & features",
  "settings.plan.aiFeaturesNoPlan": "Compare plans & features",
  "settings.plan.bulletSolo":
    "Solo: unlimited sent quotes (email, PDF, or link), owner-only, customer portal & core workflow, in-app messaging, scheduled send, 1-step follow-ups",
  "settings.plan.bulletPro":
    "Pro: unlimited sent quotes (email, PDF, or link), AI quote drafting, AI quote review, voice & photo capture, 2 coworkers (add more at $15/seat), 10 crew or subcontractor view-only, 2-step follow-ups, team activity audit",
  "settings.plan.bulletAgency":
    "Agency: unlimited sent quotes (email, PDF, or link), all AI features, 10 coworkers (add more at $15/seat, no cap), unlimited crew or subcontractors, priority support, 3-step follow-ups + AI quote coach, white-label",
  "settings.plan.table.rowPrioritySupport": "Priority support",
  "settings.plan.table.headFeature": "Feature",
  "settings.plan.table.headStarter": "Starter",
  "settings.plan.table.headSolo": "Solo",
  "settings.plan.table.headPro": "Pro",
  "settings.plan.table.headAgency": "Agency",
  "settings.plan.table.rowQuotes": "Sent quotes",
  "settings.plan.table.rowTeam": "Coworker logins (full access)",
  "settings.plan.table.teamSeatsUpTo": "Up to {{n}}",
  "settings.plan.table.rowCalendar": "Company calendar",
  "settings.plan.table.rowCustomerPortal": "Customer portal",
  "settings.plan.table.rowDeposits": "Split payments / deposits",
  "settings.plan.table.rowTeamActivityAudit": "Team activity audit (who changed what)",
  "settings.plan.table.teamAuditCellSolo": "Pro & Agency",
  "settings.plan.table.followUp1": "1 step",
  "settings.plan.table.followUp2": "2 steps",
  "settings.plan.table.followUp3": "3 steps",
  "settings.plan.table.followUpCellSolo": "1-step reminders",
  "settings.plan.table.followUpCellPro": "2-step reminders + follow-ups & analytics",
  "settings.plan.table.followUpCellAgency": "3-step reminders + analytics + AI coach",
  "settings.plan.table.valueSoloOwnerWorkspace": "Owner workspace & office calendar",
  "settings.plan.table.valueSoloNewQuoteAiOnly": "—",
  "settings.plan.table.valueJorvioPortalExperience": "Jorvio-branded portal & quotes",
  "settings.plan.table.valueAgencyWhiteLabel": "Your brand on portal & quotes",
  "settings.plan.table.valueOfficeCalendarJobs": "Office calendar & jobs",
  "settings.plan.table.valueStandardSupport": "Standard support",
  "settings.plan.table.valuePrioritySupport": "Priority support",
  "settings.plan.table.rowCrew": "Crew or subcontractor calendar (view-only)",
  "settings.plan.table.rowHelper": "Quote assistant",
  "settings.plan.table.rowAiDrafting": "AI quote drafting (Build with AI)",
  "settings.plan.table.rowVoicePhoto": "Voice & photo notes capture",
  "settings.plan.table.rowAiReview": "AI quote review",
  "settings.plan.table.rowMaterialLookup": "Live material price lookup",
  "settings.plan.table.rowFollowUps": "Follow-up automation & analytics",
  "settings.plan.table.rowWhiteLabel": "Customer-facing branding",
  "settings.plan.table.valuePriorityIncluded": "Included",
  "settings.plan.table.valuePriorityNotIncluded": "—",
  "settings.plan.table.valueOwnerOnly": "Owner only",
  "settings.plan.table.valueNotIncluded": "—",
  "settings.plan.table.valueIncluded": "Included",
  "settings.plan.table.valueUnlimited": "Unlimited",
  "settings.plan.table.crewUpTo10": "Up to 10",
  "settings.plan.depositLine": "Split payments / deposit collection: {{status}}",
  "settings.plan.included": "Included",
  "settings.plan.upgradeProAgency": "Upgrade to Pro or Agency",
  "settings.plan.ownerBypass": "Owner billing bypass is active for this account.",
  "settings.plan.managePlans": "Manage plan & billing",
  "settings.plan.manageSubscription": "Payment & invoices",
  "settings.plan.viewPlans": "View plans",
  "settings.plan.changePlan": "Change plan",
  "settings.plan.nextChargeLabel": "Next charge",
  "settings.plan.nextChargeOwnerBypass": "Owner account — billing is bypassed for testing.",
  "settings.plan.nextChargeNoPlan": "No upcoming charge — start a plan to unlock quote creation.",
  "settings.plan.nextChargeTrial": "First charge on {{date}} after your trial ends — about {{amount}}.",
  "settings.plan.nextChargeTrialHeadline": "First charge after trial ends on {{date}}.",
  "settings.plan.nextChargeRenewal": "Renews on {{date}} — {{amount}}{{interval}}.",
  "settings.plan.nextChargeRenewalHeadline": "Renews on {{date}}.",
  "settings.plan.nextChargeDateOnly": "Next billing date: {{date}}.",
  "settings.plan.nextChargeUnknown": "Billing date will appear once your subscription is active.",
  "settings.plan.billingIntervalAnnual": "/yr",
  "settings.plan.billingIntervalMonthly": "/mo",
  "settings.plan.billingCycleMonthly": "Billed monthly",
  "settings.plan.billingCycleAnnual": "Billed annually",
  "settings.plan.ownerManagesBilling": "Your account owner manages billing for this company.",
  "settings.plan.ownerManagesBillingShort": "Owner manages billing",
  "settings.plan.scheduledChange": "Plan change scheduled: moving to {{tier}} on {{date}}.",
  "settings.plan.statusTrialing": "Free trial",
  "settings.plan.statusActive": "Active",
  "settings.plan.statusPastDue": "Past due",
  "settings.plan.statusCanceled": "Canceled",
  "settings.plan.statusInactive": "Inactive",
  "settings.plan.unlimited": "Unlimited",
  "settings.plan.shortDesc.starter":
    "Get started: unlimited sent quotes and the core quoting workflow for one owner.",
  "settings.plan.shortDesc.solo":
    "Built for one owner: unlimited sent quotes (email, PDF, or share link) and the full quoting + jobs workflow.",
  "settings.plan.shortDesc.pro":
    "For small teams: AI quote drafting, quote assistant, AI review, voice & photo capture, crew calendar, analytics, and unlimited sent quotes.",
  "settings.plan.shortDesc.agency":
    "For larger operations: unlimited sent quotes, your branding on the portal, priority support, AI quote coach, and unlimited crew seats.",
  "settings.plan.tier.starter": "Starter",
  "settings.plan.tier.solo": "Solo",
  "settings.plan.tier.pro": "Pro",
  "settings.plan.tier.agency": "Agency",

  "settings.payout.title": "Payout account",
  "settings.payout.description":
    "Connect the bank account that receives online card payments from quote checkouts. You can send quotes by email or PDF without this. Connect when you want customers to pay by card on the quote link.",
  "settings.payout.verified":
    "Verified and ready. Card payments from quote checkouts will deposit directly to your bank.",
  "settings.payout.pending":
    "Payment setup is not complete yet. Finish setup to start accepting card payments on quote links.",
  "settings.payout.notConnected":
    "Not connected yet — that’s fine. You can still email quotes or send PDFs. Connect here when you’re ready for card checkout on the public link.",
  "settings.payout.connect": "Connect payout account",
  "settings.payout.connectHint": "Set up your bank account so customers can pay by card on quote links",
  "settings.payout.finishSetup": "Finish setup",
  "settings.payout.finishSetupHint": "Complete verification and add your bank account in Stripe",
  "settings.payout.verifying": "Stripe is reviewing your information",
  "settings.payout.verifyingHint":
    "This usually takes a few minutes, but for some accounts it can take up to 1–2 business days (most often when a bank account is added by micro-deposit). Nothing more for you to do — we'll email you the moment card payments go live.",
  "settings.payout.updateSetup": "Update payout settings",
  "settings.payout.updateSetupHint": "Change your bank account or update verification info in Stripe",
  "settings.payout.openDashboard": "View payouts & balance",
  "settings.payout.openDashboardHint": "See deposits, transaction history, and payout schedule in Stripe",
  "settings.payout.banner.verifiedTitle": "Payout setup complete",
  "settings.payout.banner.verifiedBody":
    "You're all set — customers can now pay by card on quote links and deposits will land in your bank.",
  "settings.payout.banner.verifyingTitle": "Got it — Stripe is reviewing your info",
  "settings.payout.banner.verifyingBody":
    "Usually a few minutes, but it can take up to 1–2 business days for some accounts. We'll email you the moment card payments go live — no need to check back.",
  "settings.payout.banner.incompleteTitle": "Setup wasn't finished",
  "settings.payout.banner.incompleteBody":
    "It looks like Stripe didn't get all the info it needed. Click \"Finish setup\" below to pick up where you left off — your progress is saved.",
  "settings.payout.banner.errorTitle": "Couldn't reach Stripe",
  "settings.payout.banner.errorBody":
    "We couldn't confirm your status with Stripe just now. Hit \"Check status now\" in a minute, or reload this page.",
  "settings.payout.footer":
    "Bank details stay in Stripe. Jorvio Quotes does not store raw account and routing numbers in this app. Email and PDF quotes do not require a payout account.",
  "settings.payout.depositTimingNote":
    "Stripe's standard schedule deposits funds to your bank about 2 business days after each successful payment. Your first payout may take 7–14 days while Stripe verifies the new account.",

  "settings.archive.title": "Quote storage",
  "settings.archive.description": "Manage quotes you have archived.",
  "settings.archive.button": "View archived quotes",
  "settings.archivedPageTitle": "Archived quotes & jobs",
  "settings.archivedPageSubtitle":
    "Archived quotes are hidden from the main Quotes list. Archived jobs are hidden from Active jobs.",
  "settings.archivedQuotesSection": "Archived quotes",
  "settings.archivedJobsSection": "Archived jobs",
  "settings.archivedJobsEmpty": "No archived jobs.",
  "settings.archivedJobRestore": "Restore job",
  "settings.archivedQuotesEmptyTitle": "No archived quotes",
  "settings.archivedQuotesEmptyDesc": "Archived quotes will appear here.",
  "settings.archivedDeleteQuoteAria": "Delete archived quote",

  "settings.followup.title": "Quote follow-up automation",
  "settings.followup.description":
    "Create a default reminder sequence that runs automatically after a quote is first sent.",
  "settings.followup.agencyIncluded": "Agency includes customizable follow-up automation for sent quotes.",
  "settings.followup.agencyAvailable": "Custom follow-up automation is available on the Agency plan.",
  "settings.followup.manage": "Manage automation",
  "settings.followup.viewFeature": "View Agency feature",

  "settings.owner.title": "Owner dashboard",
  "settings.owner.description":
    "Internal platform metrics, subscription analytics, and test controls for your own account.",
  "settings.owner.open": "Open owner dashboard",

  "settings.crew.title": "Crew or subcontractor calendar access",
  "settings.crew.description":
    "Share a team code so crew or subcontractors can join your company and open their team calendar view. They use the code during account setup, then sign in with email and password. Their names appear here and in \"Assign to\" on jobs and your company calendar.",
  "settings.crew.hintRotate":
    "Anyone with the code can start crew or subcontractor account setup. Creating a new code invalidates the previous code for new sign-ups—it does not remove people who were already using an old code.",
  "settings.crew.noCodeYet": "No team code yet. Generate one to enable crew or subcontractor access.",
  "settings.crew.activeCodeLabel": "Your active team code",
  "settings.crew.copyCode": "Copy code",
  "settings.crew.codeHiddenTitle": "A team code is active, but it isn't stored for display.",
  "settings.crew.codeHiddenBody":
    "Generate a new code below to see and share it. That invalidates the old code for new sign-ups only; share the new code with anyone joining from now on.",
  "settings.crew.generating": "Generating…",
  "settings.crew.generateNew": "Generate new team code",
  "settings.crew.generateFirst": "Generate team code",
  "settings.crew.refreshRoster": "Refresh team list",
  "settings.crew.rosterTitle": "Team members (crew or subcontractor calendar)",
  "settings.crew.rosterHint":
    "People who have opened your team calendar at least once. Use these names when you assign jobs.",
  "settings.crew.noSignins":
    "No sign-ins yet. Share your team code from the login page; this list updates when someone opens the team calendar.",
  "settings.crew.tableName": "Name",
  "settings.crew.tableLastActive": "Last active",
  "settings.crew.tableVisits": "Visits",
  "settings.crew.toastCodeFail": "Could not update code",
  "settings.crew.toastCodeUpdatedTitle": "Team code updated",
  "settings.crew.toastCodeUpdatedDesc":
    "Copy the new code for new crew or subcontractors. The previous code no longer works for new sign-ups; people already on the team keep access.",

  "settings.cooperator.title": "Co-operator access",
  "settings.cooperator.description":
    "Trusted teammate: same quote and calendar power as you, except they cannot delete the company account. Separate code from the crew or subcontractor calendar.",
  "settings.cooperator.hint":
    "They sign up at Join with code after you share this invite. The crew or subcontractor calendar code is only for field calendar access.",
  "settings.cooperator.noCodeYet": "No co-operator code yet. Generate one to invite a partner.",
  "settings.cooperator.activeCodeLabel": "Active co-operator invite code",
  "settings.cooperator.copyCode": "Copy code",
  "settings.cooperator.codeHiddenTitle": "A code is active but not stored for display.",
  "settings.cooperator.codeHiddenBody":
    "Generate a new code to show a copyable value. That invalidates the old invite for new sign-ups only; people who already joined keep access.",
  "settings.cooperator.generating": "Generating…",
  "settings.cooperator.generateNew": "Generate new co-operator code",
  "settings.cooperator.generateFirst": "Generate co-operator code",
  "settings.cooperator.toastCodeFail": "Could not update co-operator code",
  "settings.cooperator.toastCodeUpdatedTitle": "Co-operator code updated",
  "settings.cooperator.toastCodeUpdatedDesc":
    "Share the new code for new co-operators. The previous invite no longer works for new sign-ups; anyone already joined keeps access.",

  "settings.crewInbox.title": "Crew or subcontractor messages",
  "settings.crewInbox.subtitle":
    "Chat with crew or subcontractors who signed in using your team code. They see this thread on their team calendar page after they join.",
  "settings.crewInbox.emptyList":
    "No conversations yet. When someone uses your team code, they'll appear in the team list above. Then you can message them here.",
  "settings.crewInbox.teamMember": "Team member",
  "settings.crewInbox.pickPerson": "Pick a person",
  "settings.crewInbox.selectPrompt": "Select someone to view messages or start a new thread.",
  "settings.crewInbox.loadingMsgs": "Loading messages…",
  "settings.crewInbox.noMsgs": "No messages yet. Say hello below.",
  "settings.crewInbox.office": "Office",
  "settings.crewInbox.crew": "Crew",
  "settings.crewInbox.writePlaceholder": "Write a message…",
  "settings.crewInbox.sending": "Sending…",
  "settings.crewInbox.send": "Send",
  "settings.crewInbox.refreshList": "Refresh list",
  "settings.crewInbox.toastLoadFail": "Could not load crew inbox",
  "settings.crewInbox.toastLoadMsgsFail": "Could not load messages",
  "settings.crewInbox.toastChooseMember": "Choose a team member and enter a message",
  "settings.crewInbox.toastSendFail": "Send failed",
  "settings.crewInbox.toastSent": "Sent",

  "settings.logo.title": "Logo",
  "settings.logo.description": "Shown on customer-facing quotes. Square image works best.",
  "settings.section.personal.title": "Personal profile",
  "settings.section.personal.description": "Language, your name, login email, personal phone, and password.",
  "settings.section.business.title": "Business details",
  "settings.section.business.description":
    "Company identity, trade, branding, and address that appear with your business details on customer quotes where applicable.",
  "settings.profile.personalPhone": "Personal phone",
  "settings.profile.personalPhoneHelp":
    "Stored for your records only — not shown on customer quotes. The app does not send SMS or verify phone numbers.",
  "settings.profile.quoteContactEmail": "Quote contact email",
  "settings.profile.quoteContactEmailHelp":
    "Shown with your business details on PDFs and the quote preview. Leave blank to use your login email.",
  "settings.profile.phoneOnQuotesHelp": "Shown with your business details on PDFs and the quote preview.",
  "settings.profile.requiredFieldMarker": "Required",
  "settings.validation.quoteEmail": "Enter a valid email or leave blank.",
  "settings.validation.vanitySubdomain": "Use only letters, numbers, or dashes (no spaces).",
  "settings.nav.personal": "Personal",
  "settings.nav.business": "Business",
  "settings.nav.logos": "Logos",
  "settings.nav.ai": "AI & text",
  "settings.nav.integrations": "Integrations",
  "settings.nav.plan": "Plan",
  "settings.nav.payout": "Payout",
  "settings.nav.archive": "Archive",
  "settings.nav.priceBook": "Price book",
  "settings.nav.ops": "Ops",
  "settings.nav.followUp": "Follow-up",
  "settings.nav.owner": "Owner",
  "settings.nav.crew": "Crew or subs",
  "settings.nav.teamCode": "Team code",
  "settings.nav.team": "People",
  "settings.nav.legal": "Legal",
  "settings.nav.account": "Account",
  "settings.nav.group.profileCompany": "Profile & company",
  "settings.nav.group.billingWorkflow": "Billing & workflow",
  "settings.nav.group.team": "Team",
  "settings.nav.group.account": "Account",
  "settings.nav.group.owner": "Owner",
  "settings.tab.personal": "Personal profile",
  "settings.tab.company": "Business details",
  "settings.tab.reminders": "Smart reminders",
  "settings.tab.brand": "Logos",
  "settings.tab.aiQuotes": "Quote AI",
  "settings.tab.reviews": "Reviews",
  "settings.tab.plan": "Plan and billing",
  "settings.tab.payments": "Payouts",
  "settings.tab.workspace": "Archive & quote tools",
  "settings.tab.access": "Access codes",
  "settings.tab.people": "People & roster",
  "settings.tab.account": "Account & support",
  "settings.tab.owner": "Owner dashboard",

  "settings.profile.title": "Profile",
  "settings.profile.description":
    "What appears on customer quotes: company identity, branding, and contact info.",
  "settings.profile.yourName": "Your name",
  "settings.profile.companyName": "Company name",
  "settings.profile.phone": "Phone",
  "settings.profile.trade": "Trade",
  "settings.profile.selectTrade": "Select trade",
  "settings.profile.tradeCustom": "Your trade or specialty",
  "settings.profile.tradeCustomPlaceholder": "e.g. Pool service, appliance repair, handyman",
  "settings.profile.brandColor": "Brand color",
  "settings.profile.licenseNumber": "License number",
  "settings.profile.vanityQuoteSubdomain": "Custom quote link subdomain (Agency)",
  "settings.profile.vanityQuoteSubdomainHelp":
    "Set an Agency-only subdomain to brand customer quote links (e.g. acmeelectric -> https://acmeelectric.jorvioquotes.com/p/<token>).",
  "settings.profile.address": "Address",
  "settings.profile.city": "City",
  "settings.profile.state": "State",
  "settings.profile.zip": "ZIP",
  "settings.profile.emailLabel": "Email",
  "settings.profile.emailCurrent": "Current account email:",
  "settings.profile.emailUpdate": "Update email",
  "settings.profile.emailSending": "Sending…",
  "settings.profile.emailHelp": "Your new email becomes active only after you click the confirmation link we’ll send to it.",
  "settings.profile.emailEnter": "Enter email",
  "settings.profile.emailUpdateFailed": "Could not update email",
  "settings.profile.emailConfirmationSentTitle": "Confirmation email sent",
  "settings.profile.emailConfirmationSentDesc": "Click the link sent to {{email}} to switch your account email.",
  "settings.profile.emailPendingTitle": "Confirmation email sent to {{email}}.",
  "settings.profile.emailPendingBody": "Click the link in that inbox to switch your account email. Your current email stays active until you do.",
  "settings.profile.emailPendingResend": "Resend",
  "settings.profile.emailPendingDismiss": "Dismiss",

  "settings.trade.hvac": "HVAC",
  "settings.trade.plumbing": "Plumbing",
  "settings.trade.electrical": "Electrical",
  "settings.trade.roofing": "Roofing",
  "settings.trade.landscaping": "Landscaping",
  "settings.trade.painting": "Painting",
  "settings.trade.general": "General contractor",
  "settings.trade.other": "Other",

  "settings.business.title": "AI instructions & defaults",
  "settings.business.description":
    "One place to tell the AI how to write your quotes and what standard info to include on them.",
  "settings.business.reuseLabel": "Text to reuse on quotes",
  "settings.business.reusePlaceholder":
    "e.g. \"Service call / trip fee: $89, waived if work is approved.\" Deposit 50% on jobs over $1,000.",
  "settings.business.reuseHelp":
    "Optional. Saved here so you don't retype it. Add it to a quote and it shows on the quote your customer sees.",
  "settings.business.aiLabel": "AI instructions & standard info",
  "settings.business.aiPlaceholder":
    "e.g. \"Always 40% deposit up front. Mention our 1-year workmanship warranty. Trip fee $89, waived if work is approved. Never use em-dashes.\"",
  "settings.business.aiHelp":
    "Optional. One box for both: rules for how the AI writes your quotes (tone, formatting) and standard info to put on them (deposit %, fees, warranty, policies). The AI follows the rules and adds the customer-facing parts to each quote.",

  "settings.validation.tradeCustom": "Describe your trade or specialty",

  "quotes.title": "Quotes",
  "quotes.subtitle": "All quotes",
  "quotes.searchPlaceholder": "Search title or summary…",
  "quotes.searchAria": "Search quotes",
  "quotes.sort.placeholder": "Sort by",
  "quotes.sortAria": "Sort quotes",
  "quotes.sort.updated_desc": "Last edited",
  "quotes.sort.created_desc": "Recently created",
  "quotes.sort.sent_desc": "Recently sent",
  "quotes.sort.total_desc": "Highest total",
  "quotes.sort.title_asc": "Title A–Z",
  "quotes.statusFilterPlaceholder": "Status",
  "quotes.statusFilterAria": "Filter by status",
  "quotes.status.all": "All statuses",
  "quotes.status.draft": "Draft",
  "quotes.status.sent": "Sent",
  "quotes.status.viewed": "Viewed",
  "quotes.status.viewedStalled": "Viewed, no decision (4+ days)",
  "quotes.status.accepted": "Accepted",
  "quotes.status.declined": "Declined",
  "quotes.status.expired": "Expired",
  "quotes.paymentBadge.paid": "Paid",
  "quotes.paymentBadge.depositPaid": "Deposit paid",
  "quotes.paymentBadge.refunded": "Refunded",
  "quotes.newQuote": "New quote",
  "quotes.loadErrorTitle": "Could not load quotes",
  "quotes.loadErrorDesc": "Request failed",
  "quotes.emptyTitle": "No quotes yet",
  "quotes.emptyDesc":
    "Create a quote with AI line items, then send a link your customer can open on any device.",
  "quotes.addCustomerFirst": "Add a customer first",
  "quotes.noMatchTitle": "No matching quotes",
  "quotes.noMatchDesc": "Try a different search, status, date range, or sort option.",
  "quotes.clearFilters": "Clear filters",
  "quotes.loadMore": "Load more",
  "quotes.loadingMore": "Loading more…",
  "quotes.shownOfTotal": "Showing {shown} of {total}",
  "quotes.dateUpdatedFrom": "Edited from",
  "quotes.dateUpdatedTo": "Edited to",
  "quotes.dateRangeHint": "Filter by last edited date (your local timezone).",
  "quotes.priceBookSearchPlaceholder": "Search catalog…",
  "quotes.card.customer": "Customer",
  "quotes.card.validUntil": "Valid until",
  "quotes.card.total": "Total",
  "quotes.card.updated": "Updated",
  "quotes.card.archiveTitle": "Archive quote",
  "quotes.card.unarchiveTitle": "Unarchive quote",
  "quotes.card.unarchive": "Unarchive",
  "quotes.card.copyLink": "Copy link",
  "quotes.card.quickNote": "Quick note",
  "quotes.card.quickNoteTitle": "List note",
  "quotes.card.quickNotePlaceholder": "One line for your team (not shown to customers)",
  "quotes.card.quickNoteSave": "Save",
  "quotes.card.quickNoteSaved": "Note saved",
  "quotes.card.quickNoteAddTitle": "Add a quick note",
  "quotes.card.quickNoteDialogTitle": "Add quick note",
  "quotes.card.expiredBadge": "Expired",
  "quotes.card.expiresTodayBadge": "Expires today",
  "quotes.card.expiresInDaysBadge": "Expires in {{days}} days",
  "quotes.card.expiresInOneDayBadge": "Expires in 1 day",
  "quotes.card.notOpenedYet": "Not opened yet (no views)",
  "quotes.card.openedTimes": "Opened {{count}}×",
  "quotes.card.openedLast": "Last {{date}}",
  "quotes.card.movedToJobs": "Moved to Jobs",
  "quotes.card.movedToJobsTitle": "This quote was accepted and is now tracked in Jobs",
  "quotes.card.movedToJobsNote": "Accepted… it will appear in Jobs now.",
  "quotes.pricing.standardLabel": "Set prices on each line",
  "quotes.pricing.marginTargetLabel": "Cost + markup %",
  "quotes.pricing.standardTooltip":
    "Each line price is what the customer pays. When you use the quote assistant, it can suggest typical rates for your trade.",
  "quotes.pricing.marginTargetTooltip":
    "Enter your cost on each line. The customer total adds your markup percent on top.",
  "quotes.toast.archiveFailed": "Archive failed",
  "quotes.toast.archived": "Quote archived",
  "quotes.toast.undo": "Undo",
  "quotes.toast.undoArchiveAlt": "Put this quote back on your active list",
  "quotes.toast.unarchiveFailed": "Unarchive failed",
  "quotes.toast.restored": "Quote restored",

  "dashboard.subtitle": "Overview of quotes, pipeline, and payments",
  "dashboard.scheduleNudgeTitle": "Job window ending soon",
  "dashboard.scheduleNudgeIntro":
    "These accepted jobs end on-site within about two days (or just did). Update the quote schedule if you need more time, or remind the customer about a balance.",
  "dashboard.scheduleNudgeEnd": "Scheduled end",
  "dashboard.scheduleNudgeBalance": "Balance remaining (estimate)",
  "dashboard.scheduleNudgeOpenQuote": "Open quote",
  "dashboard.scheduleNudgeBalanceEmail": "Balance email",
  "dashboard.recentQuotes": "Recent quotes",
  "dashboard.recent.empty": "No quotes yet.",
  "dashboard.recent.createFirst": "Create your first quote",
  "dashboard.recent.viewAll": "View all",
  "dashboard.recent.colQuote": "Quote",
  "dashboard.recent.colCustomer": "Customer",
  "dashboard.recent.colStatus": "Status",
  "dashboard.recent.colAmount": "Amount",
  "dashboard.recent.colUpdated": "Updated",
  "dashboard.stats.summary": "{{total}} quotes · {{accepted}} accepted · {{winRate}} win rate",
  "dashboard.stats.totalQuotes": "Total quotes",
  "dashboard.stats.totalQuotesHint": "Active quotes (excludes archived).",
  "dashboard.stats.quotesPending": "Quotes pending",
  "dashboard.stats.quotesPendingHint":
    "Sent or opened—not accepted, declined, or expired yet. Drafts excluded.",
  "dashboard.stats.totalProfit": "Total profit",
  "dashboard.stats.totalProfitHint":
    "What the customer pays on accepted quotes, minus your costs. Includes accepted jobs even when you got paid outside the app.",
  "dashboard.stats.totalProfitHintStarter":
    "What the customer pays on accepted quotes, minus the costs you entered on those quotes.",
  "dashboard.stats.jobsCompleted": "Jobs completed",
  "dashboard.stats.jobsCompletedHint": "Jobs marked complete in Jobs.",
  "dashboard.stats.quoteWinRate": "Win rate",
  "dashboard.stats.quoteWinRateHint": "Accepted quotes divided by all quotes you have sent.",
  "dashboard.stats.profitLast30": "Profit from accepted quotes (last 30 days)",
  "dashboard.stats.profitLast30Hint":
    "Profit on the day each quote was accepted. Same math as total profit, including jobs paid outside the app.",
  "dashboard.stats.profitLast30HintStarter":
    "Profit on the day each quote was accepted. Same math as total profit: quote total minus your costs.",
  "dashboard.stats.profitChartEmptyTitle": "No accepted quotes yet",
  "dashboard.stats.profitChartEmptyBody":
    "Profit by day appears here when customers accept quotes. Based on quote total minus your internal costs.",
  "dashboard.stats.profitChartOlderTitle": "No accepted quotes in the last 30 days",
  "dashboard.stats.profitChartOlderBody":
    "Your total profit above includes older accepted quotes. This chart only covers the last 30 days, so it stays flat until a new quote is accepted.",
  "dashboard.stats.showHint": "Show explanation",
  "dashboard.stats.hideHint": "Hide explanation",
  "dashboard.onboarding.welcomeTitle": "Welcome — let's get your first quote out",
  "dashboard.onboarding.welcomeDesc":
    "Once you send quotes, stats here (sent, viewed, wins, open quotes) update on their own.",
  "dashboard.onboarding.step1Title": "Add a customer",
  "dashboard.onboarding.step1Body": "Keep contact info in one place for repeat work.",
  "dashboard.onboarding.step2Title": "Create your first quote",
  "dashboard.onboarding.step2Body": "Draft line items with AI, then edit and send.",
  "dashboard.onboarding.step3Title": "Send the link",
  "dashboard.onboarding.step3Body": "Customers open quotes on mobile and can accept with a signature.",
  "dashboard.onboarding.go": "Go →",
  "dashboard.onboarding.customers": "Customers",
  "dashboard.onboarding.activityTitle": "Activity preview",
  "dashboard.onboarding.activityDesc": "After you send quotes, trends show up here.",
  "dashboard.onboarding.bullet1": "Sent & viewed counts roll up by month",
  "dashboard.onboarding.bullet2": "Open quote totals and won revenue from accepted quotes",
  "dashboard.team.title": "Team calendar",
  "dashboard.team.description":
    "On jobs, use Assign to for whoever should do the work. Crew or subcontractors only see jobs assigned to them on their team calendar (/crew) after they sign in with your team code.",
  "dashboard.team.calendarCode": "View team code",
  "dashboard.team.crewAccessCodeHelp":
    "Open the button to show or copy the code. Crew or subcontractors go to /crew, enter the code once, sign in, and use the same name you pick in Assign to.",
  "dashboard.team.codeHint":
    "Create a team code in Settings → Team when you want crew or subcontractors to see their assignments on the calendar.",
  "dashboard.team.sharingOn": "Team calendar is enabled",
  "dashboard.team.since": "Team code active since {{date}}",
  "dashboard.team.teamCodeActive": "Team code is active.",
  "dashboard.team.signinsNone":
    "No crew or subcontractor sign-ins yet. Share the code and have each person open /crew and sign in with the same name you use in Assign to.",
  "dashboard.team.signinsOne":
    "1 person has signed in at /crew — check the name they entered below.",
  "dashboard.team.signinsMany":
    "{{count}} crew members have signed in to the team calendar — check the names they entered below.",
  "dashboard.team.colName": "Name they entered",
  "dashboard.team.colVisits": "Visits",
  "dashboard.team.colLastOpened": "Last opened",

  ...EXTENDED_EN,
};

const es: MessageDict = {
  ...en,
  "nav.dashboard": "Panel",
  "nav.calendar": "Calendario",
  "nav.quotes": "Cotizaciones",
  "nav.newQuote": "Nueva cotización",
  "nav.customers": "Clientes",
  "nav.templates": "Plantillas",
  "nav.settings": "Ajustes",
  "nav.home": "Inicio",
  "nav.new": "Nueva",
  "nav.clients": "Clientes",
  "nav.more": "Más",
  "nav.billing": "Facturación",
  "nav.help": "Ayuda",
  "nav.primary": "Principal",
  "billingGate.subtitle": "Empieza un plan antes de crear tu primera cotización.",
  "billingGate.cardTitle": "Se necesita un plan",
  "billingGate.cardDesc":
    "Tu prueba gratis terminó. Agrega un plan para seguir creando y enviando cotizaciones. Tus cotizaciones y clientes existentes están seguros.",
  "billingGate.browseNote":
    "Puedes seguir navegando por la app, pero crear cotizaciones requiere un plan activo después de la prueba.",
  "billingGate.choosePlan": "Elegir un plan",
  "billingGate.backToDashboard": "Volver al panel",
  "billingBanner.platformTrial.one":
    "Te queda 1 día de prueba gratis. Elige un plan y agrega tu tarjeta antes de que termine para mantener el acceso completo.",
  "billingBanner.platformTrial.many":
    "Te quedan {days} días de prueba gratis. Elige un plan y agrega tu tarjeta antes de que termine para mantener el acceso completo.",
  "billingBanner.pickPlan": "Elegir un plan",
  "billingBanner.stripeTrialing.one":
    "Te queda 1 día de tu prueba inicial. Agrega un método de pago antes de que termine para mantener el acceso completo.",
  "billingBanner.stripeTrialing.many":
    "Te quedan {days} días de tu prueba inicial. Agrega un método de pago antes de que termine para mantener el acceso completo.",
  "billingBanner.addPayment": "Agregar método de pago",
  "billingBanner.billing": "Facturación",
  "billingBanner.ownerPromo.one":
    "Te queda aproximadamente 1 día de tu plan promocional gratis. Agrega un método de pago en Facturación antes de que termine para mantener el acceso completo.",
  "billingBanner.ownerPromo.many":
    "Te quedan aproximadamente {days} días de tu plan promocional gratis. Agrega un método de pago en Facturación antes de que termine para mantener el acceso completo.",
  "billingBanner.promoPick":
    "Tu acceso promocional terminó. Elige un plan de pago para seguir usando Jorvio. Te recomendamos {tier} (el nivel que tenías en la promoción).",
  "billingBanner.choosePlan": "Elegir plan",
  "billingBanner.trialEnded":
    "Tu prueba gratis terminó. Tu espacio de trabajo es de solo lectura. Elige un plan y agrega tu tarjeta para volver a crear, enviar y guardar.",
  "billingBanner.paymentFailed":
    "No pudimos procesar tu último pago. Tu espacio de trabajo es de solo lectura hasta que actualices la tarjeta. Aún puedes ver cotizaciones y trabajos.",
  "billingBanner.updatePayment": "Actualizar pago",
  "a11y.skipToMain": "Saltar al contenido principal",
  "nav.followUpsOpenQuotes": "Seguimiento y análisis",
  "nav.followUpsShort": "Seguimiento",
  "nav.messages": "Mensajes",
  "nav.jobs": "Trabajos",
  "jobs.title": "Trabajos",
  "jobs.subtitle":
    "Cada trabajo en una lista, con sus cotizaciones vinculadas, su programación y el saldo pendiente.",
  "jobs.unscheduled": "Sin fecha",
  "jobs.noCustomerLinked": "Sin cliente vinculado",
  "jobs.tab.active": "Activos",
  "jobs.tab.completed": "Completados",
  "jobs.tab.archived": "Archivados",
  "jobs.emptyTitle": "Aún no hay trabajos",
  "jobs.emptyDesc": "Cree un trabajo manual o acepte una cotización para generar uno.",
  "jobs.newJob": "Nuevo trabajo",
  "jobs.backDashboard": "Volver al panel",
  "jobs.backToList": "Todos los trabajos",
  "jobs.loadErrorTitle": "No se pudieron cargar los trabajos",
  "jobs.loadErrorDesc": "Inténtelo de nuevo en un momento.",
  "jobs.scheduled": "Programado",
  "jobs.quotesLinked": "Cotizaciones",
  "jobs.completedLabel": "Completado",
  "jobs.openDetail": "Abrir →",
  "jobs.fieldTitle": "Título del trabajo",
  "jobs.fieldTitlePlaceholder": "ej. Reforma cocina Smith",
  "jobs.fieldStartOptional": "Inicio (opcional)",
  "jobs.fieldEndOptional": "Fin (opcional)",
  "jobs.fieldStartDate": "Fecha de inicio",
  "jobs.fieldEndDate": "Fecha de fin",
  "jobs.fieldTimeOptional": "Hora",
  "jobs.schedulingSectionTitle": "Programación (opcional)",
  "jobs.scheduleDefaultsHint":
    "Elija la fecha de inicio y/o fin. Deje la hora vacía para usar 9:00 para el inicio y 17:00 para el fin (hora local). La hora solo aparece si usted la elige.",
  "jobs.fieldSite": "Dirección del sitio",
  "jobs.fieldNotes": "Notas",
  "jobs.cancel": "Cancelar",
  "jobs.create": "Crear",
  "jobs.saving": "Guardando…",
  "jobs.createNeedTitle": "Indique un título.",
  "jobs.createFailed": "No se pudo crear el trabajo",
  "jobs.createOk": "Trabajo creado",
  "jobs.payment.paid": "Pagado",
  "jobs.payment.partial": "Parcialmente pagado",
  "jobs.payment.unpaid": "Sin pagar",
  "jobs.status.completed": "Completado",
  "jobs.status.scheduled": "Programado",
  "jobs.status.notScheduled": "Sin programar",
  "jobs.photos.sectionTitle": "Fotos antes y después",
  "jobs.photos.before": "Antes",
  "jobs.photos.after": "Después",
  "jobs.photos.none": "Aún no hay fotos",
  "jobs.photos.hint": "Opcional. El cliente puede verlas en su portal.",
  "jobs.photos.beforeLabel": "Fotos antes",
  "jobs.photos.afterLabel": "Fotos después",
  "jobs.photos.addBefore": "Añadir antes",
  "jobs.photos.addAfter": "Añadir después",
  "jobs.photos.remove": "Quitar",
  "jobs.photos.uploading": "Subiendo…",
  "jobs.photos.markComplete": "Marcar completado",
  "jobs.photos.markCompleteNoAfterTitle": "¿Marcar completado sin fotos después?",
  "jobs.photos.markCompleteNoAfterBody":
    "No has añadido fotos después. A los clientes suele gustarles ver el trabajo terminado. Aun así puedes marcar el trabajo como completado.",
  "jobs.photos.markCompleteConfirm": "Marcar completado igualmente",
  "jobs.detail.loadError": "No se pudo cargar el trabajo",
  "jobs.detail.saveFailed": "No se pudo guardar",
  "jobs.detail.readOnlyTitle": "Espacio de trabajo solo lectura",
  "jobs.detail.readOnlyBody": "Añada un plan o corrija la facturación en Ajustes → Facturación para volver a editar trabajos.",
  "jobs.detail.saved": "Guardado",
  "jobs.detail.actionFailed": "No se pudo actualizar",
  "jobs.detail.attachFailed": "No se pudo vincular la cotización",
  "jobs.detail.attached": "Cotización vinculada",
  "jobs.detail.detached": "Cotización desvinculada",
  "jobs.detail.deleteConfirm": "¿Eliminar este trabajo? Se limpian enlaces del calendario.",
  "jobs.detail.deleteFailed": "No se pudo eliminar",
  "jobs.detail.deleted": "Trabajo eliminado",
  "jobs.detail.notFound": "Trabajo no encontrado.",
  "jobs.detail.edit": "Editar detalles",
  "jobs.detail.markIncomplete": "Marcar incompleto",
  "jobs.detail.markComplete": "Marcar completado",
  "jobs.detail.unarchive": "Restaurar",
  "jobs.detail.archive": "Archivar",
  "jobs.detail.delete": "Eliminar",
  "jobs.detail.noSite": "Sin dirección",
  "jobs.detail.paymentTitle": "Pagos (cotizaciones vinculadas)",
  "jobs.detail.percentPaid": "{{pct}}% pagado",
  "jobs.detail.ofDue": "de",
  "jobs.detail.scheduleTitle": "Calendario y notas",
  "jobs.detail.scheduleHint": "Aparece en su calendario cuando hay fechas.",
  "jobs.detail.scheduleDayPickerTitle": "Calendario del trabajo",
  "jobs.detail.scheduleDayPickerHint":
    "Haga clic en un día para añadirlo o quitarlo. Los días elegidos se sincronizan con el calendario de la empresa (9:00–17:00 hora local). Se guarda solo.",
  "jobs.detail.scheduleDayPickerSaving": "Guardando…",
  "jobs.detail.scheduleRangeTitle": "Rango de fechas y horas",
  "jobs.detail.scheduleStartDate": "Fecha de inicio",
  "jobs.detail.scheduleEndDate": "Fecha de fin",
  "jobs.detail.scheduleStartTime": "Hora de inicio",
  "jobs.detail.scheduleEndTime": "Hora de fin",
  "jobs.detail.scheduleRepeatDays": "Repetir en días",
  "jobs.detail.excludeWeekends": "Excluir fines de semana",
  "jobs.detail.weekendDisabledHint": "Desactive «Excluir fines de semana» para programar sábado o domingo.",
  "jobs.detail.scheduleRepeatHint":
    "Deje todos los botones de día sin marcar para contar cada día natural entre la fecha de inicio y la de fin. Active solo los días en que el trabajo debe repetirse dentro de ese rango (por ejemplo solo mar y jue). «Excluir fines de semana» significa de lunes a viernes.",
  "jobs.detail.configureSchedule": "Configurar calendario",
  "jobs.detail.configureScheduleDialogDesc":
    "Agregue a las personas de este trabajo y defina los días de trabajo de cada una. Haga clic en los días del calendario para activarlos o desactivarlos. Es solo interno: no cambia nada de lo que ve el cliente.",
  "jobs.detail.scheduleCardTeaser":
    "Use Configurar calendario para agregar miembros del equipo y definir los días de trabajo de cada uno. Solo interno: el cliente no lo ve. Las notas siguen en esta página.",
  "jobs.detail.scheduleCalendarTitle": "Calendario de trabajo",
  "jobs.detail.scheduleCalendarNeedDates":
    "Indique primero la fecha de inicio (y la de fin si el trabajo dura más de un día). Luego podrá marcar o desmarcar días en la cuadrícula.",
  "jobs.detail.schedulePrevMonth": "Mes anterior",
  "jobs.detail.scheduleNextMonth": "Mes siguiente",
  "jobs.detail.scheduleSaveAndClose": "Guardar y cerrar",
  "jobs.detail.clearSchedule": "Borrar calendario",
  "jobs.detail.notesPlaceholder": "Acceso, estacionamiento, materiales, preferencias del cliente. Visible para su equipo en este trabajo.",
  "jobs.detail.clickToAddSite": "Pulse para añadir la dirección del sitio",
  "jobs.detail.editDialogHint":
    "Use Calendario y notas en esta página para el calendario de trabajo. Aquí puede cambiar el título, la dirección y las notas.",
  "jobs.detail.starts": "Inicio",
  "jobs.detail.ends": "Fin",
  "jobs.detail.noStart": "Sin fecha de inicio — edite para añadirla.",
  "jobs.detail.invalidScheduleStart": "Fecha u hora de inicio no válida. Use una fecha correcta o deje la hora vacía (9:00).",
  "jobs.detail.invalidScheduleEnd": "Fecha u hora de fin no válida. Use una fecha correcta o deje la hora vacía (17:00).",
  "jobs.detail.notesLabel": "Notas",
  "jobs.detail.quotesTitle": "Cotizaciones vinculadas",
  "jobs.detail.attachQuote": "Vincular cotización",
  "jobs.detail.noQuotesToAttach": "No hay cotizaciones aceptadas para vincular.",
  "jobs.detail.pickQuote": "Cotización",
  "jobs.detail.pickPlaceholder": "Elija una cotización",
  "jobs.detail.link": "Vincular",
  "jobs.detail.noQuotes": "Aún sin cotizaciones vinculadas.",
  "jobs.detail.primary": "Principal",
  "jobs.detail.makePrimary": "Hacer principal",
  "jobs.detail.detach": "Desvincular",
  "jobs.detail.save": "Guardar",
  "jobs.detail.paymentEdit": "Editar montos de pago manualmente",
  "jobs.detail.paymentEditShort": "Editar montos",
  "jobs.detail.paymentEditDesc":
    "Indique lo pagado y, si hace falta, un monto adeudado distinto por cotización. Deje el monto adeudado vacío para usar el total de la cotización.",
  "jobs.detail.paymentFromQuote": "Según cotización:",
  "jobs.detail.paymentAmountDue": "Monto adeudado",
  "jobs.detail.paymentAmountDueHint": "Vacío = total de la cotización. Escriba un número para sobrescribir.",
  "jobs.detail.paymentAmountPaid": "Monto pagado",
  "jobs.detail.paymentStripeNote":
    "Los pagos con tarjeta por Stripe siguen actualizando lo pagado; ajuste aquí para efectivo, cheques o correcciones.",
  "jobs.detail.paymentStripeFeeHint":
    "Estos totales son los montos de la cotización (lo que usted gana). Si pasa las tarifas de tarjeta al cliente, su enlace de pago puede mostrar cifras un poco más altas; esa diferencia es la tarifa de Stripe, no ingresos faltantes.",
  "jobs.detail.paymentStripePayoutHint":
    "Los pagos con tarjeta actualizan el total pagado aquí de inmediato, pero Stripe deposita los fondos en su banco según su calendario estándar — normalmente unos 2 días hábiles después de cada pago. El primer depósito puede tardar de 7 a 14 días mientras Stripe verifica su cuenta.",
  "jobs.detail.paymentStripePolicyLink": "Política de pagos de Stripe",
  "jobs.detail.paymentHideStripeInfo": "Ocultar",
  "jobs.detail.paymentShowStripeInfo": "Mostrar info de pagos",
  "jobs.detail.paymentInvalidPaid": "Indique un monto pagado válido (0 o más).",
  "jobs.detail.paymentInvalidDue": "Indique un monto adeudado válido o déjelo vacío.",
  "jobs.detail.paymentSaveFailed": "No se pudieron guardar los montos",
  "jobs.detail.paymentSaved": "Montos guardados",
  "jobs.detail.requestBalancePayment": "Solicitar pago restante",
  "jobs.detail.balanceReminderOpening": "Cargando…",
  "jobs.detail.balanceReminderTitle": "Correo al cliente — saldo pendiente",
  "jobs.detail.balanceReminderDesc":
    "Envíe un recordatorio con el mismo enlace seguro de la cotización original.",
  "jobs.detail.balanceReminderEmailHelp":
    "Si tiene dudas sobre el monto o la fecha, abra el enlace de su cotización y envíe un mensaje a su contratista allí.",
  "jobs.detail.balanceReminderSubjectLabel": "Asunto",
  "jobs.detail.balanceReminderMessageLabel": "Mensaje",
  "jobs.detail.balanceReminderSubjectPrefix": "Saldo pendiente",
  "jobs.detail.balanceReminderSend": "Enviar correo",
  "jobs.detail.balanceReminderSending": "Enviando…",
  "jobs.detail.balanceReminderSent": "Recordatorio enviado",
  "jobs.detail.balanceReminderSentDesc": "El cliente recibirá su mensaje por correo.",
  "jobs.detail.balanceReminderSendFailed": "No se pudo enviar el recordatorio",
  "jobs.detail.balanceReminderOpenFailed": "No se pudo abrir el recordatorio",
  "jobs.detail.balanceReminderUnavailable": "Recordatorio de saldo no disponible",
  "jobs.detail.balanceReminderUnavailableDesc":
    "Esta cotización no tiene saldo en línea pendiente, o los pagos divididos no están configurados.",
  "jobs.detail.balanceReminderNoShareLink": "Envíe la cotización al cliente primero para que tenga un enlace de pago.",
  "jobs.detail.balanceReminderNoEmail": "Agregue el correo del cliente en la cotización vinculada antes de enviar.",
  "jobs.detail.balanceReminderTooShort": "Mensaje demasiado corto",
  "jobs.detail.balanceReminderTooShortDesc": "Agregue un poco más de detalle antes de enviar.",
  "jobs.detail.paymentShowBreakdown": "Ver desglose",
  "jobs.detail.paymentHideBreakdown": "Ocultar desglose",
  "jobs.detail.paymentBreakdownTitle": "Por cotización",
  "jobs.detail.paymentSplitHint": "{{pct}}% de anticipo · saldo después",
  "jobs.detail.paymentDueAtEndHint": "Monto total al finalizar el trabajo",
  "jobs.detail.requestDepositPayment": "Solicitar anticipo",
  "jobs.detail.requestFullPayment": "Solicitar pago",
  "jobs.detail.depositReminderSubjectPrefix": "Anticipo pendiente",
  "jobs.detail.paymentReminderSubjectPrefix": "Pago pendiente",
  "jobs.detail.depositReminderTitle": "Correo al cliente — anticipo pendiente",
  "jobs.detail.paymentReminderTitle": "Correo al cliente — pago pendiente",
  "jobs.detail.openJob": "Abrir trabajo",
  "projects.detail.paymentTitle": "Pagos (todos los trabajos del proyecto)",
  "settings.title": "Ajustes",
  "settings.subtitle": "Perfil de empresa y lo que ven los clientes en las cotizaciones.",
  "settings.language": "Idioma",
  "settings.languageDescription":
    "Menús, botones y texto de cotización generado por IA (título, alcance, partidas).",
  "settings.saveHint": "Los cambios se guardan solo cuando hace clic en Guardar.",
  "settings.saved": "Guardado",
  "settings.save": "Guardar",
  "settings.profileUpdated": "Su perfil se actualizó.",
  "settings.saveProfile": "Guardar perfil",
  "settings.saveProfileHint":
    "Guarda idioma, datos de contacto y marca. El idioma actualiza las etiquetas en toda la app.",
  "settings.saving": "Guardando…",
  "settings.saveChanges": "Guardar todos los ajustes",
  "settings.unsavedChanges": "Tiene cambios en el perfil sin guardar.",
  "settings.offlineBanner":
    "Sin conexión. Los cambios del perfil se guardan en este dispositivo y se sincronizan al volver a estar en línea.",
  "settings.autosaveHint": "Los cambios del perfil se guardan automáticamente.",
  "settings.offlineSavedLocal":
    "Guardado en este dispositivo. Se sincronizarán con su cuenta cuando vuelva a estar en línea.",
  "settings.offlineQueuedNetwork":
    "No se pudo contactar al servidor. Los cambios quedaron en este dispositivo y se reintentarán cuando mejore la conexión.",
  "settings.onlineSynced": "Los cambios sin conexión se sincronizaron con su cuenta.",
  "settings.offlineLocalStorageFailed":
    "No se pudo guardar una copia local. Revise permisos de almacenamiento del navegador o espacio libre.",
  "settings.leaveUnsaved":
    "Tiene cambios en el perfil sin guardar. ¿Salir de esta página sin guardar?",
  "settings.partialSaveMigrationHint":
    "Se guardaron los demás campos. Si el teléfono personal o el correo de contacto no se guardan, ejecute la migración de base de datos más reciente para su proyecto.",
  "signup.title": "Cree su cuenta",
  "signup.language": "Idioma",
  "signup.languageHint":
    "Se usa en la app y en las cotizaciones con IA. Cámbielo cuando quiera en Ajustes.",
  "signup.description": "Cree su cuenta con una contraseña y luego confírmela desde su correo para terminar.",
  "signup.requiredBefore": "Los campos marcados con",
  "signup.requiredAfter": "son obligatorios.",
  "signup.fieldOptional": "(opcional)",
  "signup.email": "Correo electrónico",
  "signup.firstName": "Nombre",
  "signup.lastName": "Apellido",
  "signup.companyName": "Nombre de la empresa",
  "signup.trade": "Oficio",
  "signup.selectTrade": "Seleccione un oficio",
  "signup.tradeCustom": "Su oficio o especialidad",
  "signup.city": "Ciudad",
  "signup.state": "Estado",
  "signup.password": "Contraseña",
  "signup.confirmPassword": "Confirmar contraseña",
  "signup.createAccount": "Crear cuenta",
  "signup.creatingAccount": "Creando cuenta…",
  "signup.alreadyHaveAccount": "¿Ya tiene una cuenta?",
  "signup.signIn": "Iniciar sesión",
  "signup.consentBefore": "Acepto la",
  "signup.termsOfService": "Términos del servicio",
  "signup.privacyPolicy": "Política de privacidad",
  "signup.trades.hvac": "Climatización (HVAC)",
  "signup.trades.plumbing": "Plomería",
  "signup.trades.electrical": "Electricidad",
  "signup.trades.roofing": "Techado",
  "signup.trades.landscaping": "Jardinería",
  "signup.trades.painting": "Pintura",
  "signup.trades.general": "Contratista general",
  "signup.trades.other": "Otro",
  "public.language": "Idioma",
  "public.languageSaved": "Preferencia de idioma guardada.",
  "public.professionalEstimate": "Presupuesto",
  "public.threadTitle": "Mensaje a su contratista",
  "public.threadSubtitle":
    "El mismo hilo que la oficina de su contratista: plazos, alcance y seguimiento.",
  "public.threadEmpty":
    "Aún no hay mensajes. Pregunte o confirme detalles; responderán aquí.",
  "public.threadYourMessage": "Su mensaje",
  "public.threadPlaceholder": "p. ej. ¿Podemos empezar el martes?",
  "public.threadSend": "Enviar mensaje",
  "public.threadSending": "Enviando…",
  "public.threadYou": "Usted",
  "public.threadContractor": "Contratista",
  "messages.you": "Usted (contratista)",
  "messages.customer": "Cliente",
  "messages.translatedHint": "Traducido a su idioma",
  "messages.threadTitle": "Mensajes con el cliente",
  "messages.threadSubtitle":
    "El mismo hilo que el cliente ve en la cotización pública después del envío. Los mensajes pueden traducirse automáticamente.",
  "messages.noMessages":
    "Aún no hay mensajes. Tras enviar la cotización, su primera nota aparece en la página del cliente.",
  "messages.reply": "Responder al cliente",
  "messages.send": "Enviar al cliente",
  "messages.sending": "Enviando…",
  "messages.loading": "Cargando…",
  "messages.pageTitle": "Mensajes",
  "messages.pageSubtitle": "Consultas del portal de propietarios y conversaciones por cotización de su empresa.",
  "messages.loadFailedTitle": "No se pudieron cargar los mensajes",
  "messages.replyTooShort": "Escriba una respuesta primero.",
  "messages.replySaved": "Respuesta guardada.",
  "messages.emailNotifyTitle": "Notificaciones por correo",
  "messages.emailNotifyDescription":
    "Elija si le avisamos por correo cada vez que reciba un mensaje.",
  "messages.emailNotifyCheckbox":
    "Enviarme un correo cuando reciba un nuevo mensaje de un cliente (usa su correo de contacto de cotizaciones).",
  "messages.newMessage": "Nuevo mensaje",
  "messages.groupCustomers": "Clientes",
  "messages.groupCustomersHint": "Mensajes desde el portal y respuestas en cotizaciones enviadas",
  "messages.groupTeam": "Su equipo",
  "messages.groupTeamHint": "Personal de campo, subcontratistas y co-operadores",
  "messages.sectionPortalContact": "Solicitudes de contacto del portal de propietarios",
  "messages.emptyPortalContact": "Aún no hay mensajes del portal.",
  "messages.sectionQuoteThreads": "Hilos de mensajes en cotizaciones",
  "messages.emptyQuoteThreads":
    "Aún no hay hilos. Cuando los clientes escriban desde una cotización enviada, el hilo completo aparecerá aquí.",
  "messages.sectionTeamThreads": "Mensajes con personal y co-operadores",
  "messages.emptyTeamThreads":
    "Aún no hay conversaciones con su equipo. Inicie un nuevo mensaje arriba para escribirle a alguien de su personal.",
  "messages.replied": "Respondido",
  "messages.needsReply": "Pendiente de respuesta",
  "messages.waitingOnCustomer": "En espera del cliente",
  "messages.yourReply": "Su respuesta",
  "messages.sendReply": "Enviar respuesta",
  "messages.untitledQuote": "Cotización",
  "messages.openQuote": "Abrir cotización",
  "messages.messageCount": "mensajes",
  "messages.customerLink": "Enlace para el cliente",
  "messages.youContractor": "Usted",
  "messages.emailNotifyTapHint": "Pulse para cambiar",
  "messages.emailNotifyCloseHint": "Pulse para cerrar",
  "messages.portalThreadSubtitle": "Portal de propietarios",
  "messages.composerPlaceholder": "Mensaje…",
  "messages.composerHint": "Intro para enviar · Mayús+Intro para nueva línea",
  "messages.quoteThreadFooter": "Envíe mensajes nuevos desde la cotización; el hilo se sincroniza aquí.",
  "messages.tabAll": "Todos",
  "messages.tabPortal": "Portal",
  "messages.tabQuotes": "Cotizaciones",
  "messages.tabTeam": "Equipo",
  "messages.sourcePortal": "Portal",
  "messages.sourceQuote": "Cotización",
  "messages.sourceTeam": "Equipo",
  "messages.selectConversation": "Seleccione una conversación",
  "messages.selectConversationHint": "Elija un hilo a la izquierda para leer y responder.",
  "messages.emptyInbox": "Aún no hay conversaciones en esta pestaña.",
  "messages.backToList": "Todos los mensajes",

  "common.error": "Error",
  "common.copied": "Copiado",

  "settings.stripe.notEnabled":
    "Stripe Connect aún no está activado en esta cuenta. Actívelo en Stripe y vuelva a vincular la cuenta de pagos.",
  "settings.stripe.migration":
    "No se pudo guardar el campo de pagos. Ejecute la migración de base de datos más reciente e inténtelo de nuevo.",
  "settings.stripe.unknown": "Algo salió mal al iniciar la configuración de pagos. Inténtelo de nuevo.",
  "settings.stripe.success":
    "Se abrió correctamente la configuración de pagos en Stripe. Si completó el proceso allí, su cuenta debería quedar lista cuando Stripe termine la verificación.",

  "settings.plan.title": "Plan y facturación",
  "settings.plan.currentPlan": "Plan actual",
  "settings.plan.planStatus": "Estado del plan",
  "settings.plan.noPlan": "Sin plan",
  "settings.plan.sentQuotesUsed": "{{used}} de {{limit}} cotizaciones enviadas este mes.",
  "settings.plan.sentQuotesUsedUnlimited": "{{used}} cotizaciones enviadas este mes — ilimitadas en todos los planes.",
  "settings.plan.monthlySentQuotesUsage": "Cotizaciones enviadas este mes",
  "settings.plan.sentQuotesDraftNote":
    "Todos los planes incluyen envíos ilimitados — correo, PDF y enlace usan el mismo flujo. La vista previa y los borradores nunca cuentan.",
  "settings.plan.startPlan": "Active un plan para desbloquear la creación de cotizaciones.",
  "settings.plan.trialPrices":
    "Todos los planes comienzan con una prueba gratuita de 7 días — sin tarjeta. Después del día 7, agrega una tarjeta y elige un plan para seguir creando cotizaciones.",
  "settings.plan.compareIntro": "Compare funciones, herramientas de IA y acceso del equipo. Todos los planes incluyen envíos ilimitados.",
  "settings.plan.ownerPromoTrialCallout":
    "Prueba con código promocional: le quedan unos {{days}} día(s) naturales en su plan de regalo. Añada una tarjeta en Facturación antes de que termine para mantener el acceso.",
  "settings.plan.trialPanelLabel": "Pruebas y precios de inicio",
  "settings.plan.aiFeaturesHasPlan": "Comparar planes y funciones",
  "settings.plan.aiFeaturesNoPlan": "Comparar planes y funciones",
  "settings.plan.bulletSolo":
    "Solo: cotizaciones enviadas ilimitadas, propietario, portal del cliente y flujo principal, mensajes en la app, envío programado, 1 paso de seguimiento",
  "settings.plan.bulletPro":
    "Pro: cotizaciones enviadas ilimitadas, redacción con IA, asistente de cotización, revisión IA, captura por voz/foto, 2 compañeros (extras a $15/asiento), 10 vistas de calendario, seguimiento en 2 pasos, auditoría de actividad",
  "settings.plan.bulletAgency":
    "Agency: cotizaciones enviadas ilimitadas, todas las funciones de IA, 10 compañeros (extras a $15/asiento, sin límite), cuadrilla ilimitada, soporte prioritario, 3 pasos + AI quote coach, marca blanca",
  "settings.plan.table.rowPrioritySupport": "Priority support",
  "settings.plan.table.headFeature": "Función",
  "settings.plan.table.headStarter": "Starter",
  "settings.plan.table.headSolo": "Solo",
  "settings.plan.table.headPro": "Pro",
  "settings.plan.table.headAgency": "Agency",
  "settings.plan.table.rowQuotes": "Cotizaciones enviadas (tope mensual)",
  "settings.plan.table.rowTeam": "Inicios de sesión de compañeros (acceso completo)",
  "settings.plan.table.teamSeatsUpTo": "Hasta {{n}}",
  "settings.plan.table.rowCalendar": "Calendario de empresa",
  "settings.plan.table.rowCustomerPortal": "Portal del cliente",
  "settings.plan.table.rowDeposits": "Pagos divididos / depósitos",
  "settings.plan.table.rowTeamActivityAudit": "Auditoría de actividad del equipo (quién cambió qué)",
  "settings.plan.table.teamAuditCellSolo": "Pro y Agency",
  "settings.plan.table.followUp1": "1 paso",
  "settings.plan.table.followUp2": "2 pasos",
  "settings.plan.table.followUp3": "3 pasos",
  "settings.plan.table.followUpCellSolo": "Recordatorios en 1 paso",
  "settings.plan.table.followUpCellPro": "2 pasos + seguimiento y análisis",
  "settings.plan.table.followUpCellAgency": "3 pasos + análisis + coach de IA",
  "settings.plan.table.valueSoloOwnerWorkspace": "Espacio del propietario y calendario de oficina",
  "settings.plan.table.valueSoloNewQuoteAiOnly": "IA al crear cotización nueva",
  "settings.plan.table.valueJorvioPortalExperience": "Portal y cotizaciones con marca Jorvio",
  "settings.plan.table.valueAgencyWhiteLabel": "Su marca en portal y cotizaciones",
  "settings.plan.table.valueOfficeCalendarJobs": "Calendario de oficina y trabajos",
  "settings.plan.table.valueStandardSupport": "Soporte estándar",
  "settings.plan.table.valuePrioritySupport": "Soporte prioritario",
  "settings.plan.table.rowCrew": "Calendario de cuadrilla (solo lectura)",
  "settings.plan.table.rowHelper": "Asistente de cotización",
  "settings.plan.table.rowAiDrafting": "Redacción de cotizaciones con IA",
  "settings.plan.table.rowVoicePhoto": "Captura de notas por voz y foto",
  "settings.plan.table.rowAiReview": "Revisión IA de cotizaciones",
  "settings.plan.table.rowMaterialLookup": "Búsqueda de precios de materiales en vivo",
  "settings.plan.table.rowFollowUps": "Automatización de seguimiento y análisis",
  "settings.plan.table.rowWhiteLabel": "Imagen ante el cliente",
  "settings.plan.table.valuePriorityIncluded": "Incluido",
  "settings.plan.table.valuePriorityNotIncluded": "—",
  "settings.plan.table.valueOwnerOnly": "Solo propietario",
  "settings.plan.table.valueNotIncluded": "—",
  "settings.plan.table.valueIncluded": "Incluido",
  "settings.plan.table.valueUnlimited": "Ilimitado",
  "settings.plan.table.crewUpTo10": "Hasta 10",
  "settings.plan.depositLine": "Pagos fraccionados / depósitos: {{status}}",
  "settings.plan.included": "Incluido",
  "settings.plan.upgradeProAgency": "Actualice a Pro o Agency",
  "settings.plan.ownerBypass": "Bypass de facturación del propietario activo en esta cuenta.",
  "settings.plan.managePlans": "Gestionar plan y facturación",
  "settings.plan.manageSubscription": "Pago y facturas",
  "settings.plan.viewPlans": "Ver planes",
  "settings.plan.changePlan": "Cambiar plan",
  "settings.plan.nextChargeLabel": "Próximo cargo",
  "settings.plan.nextChargeOwnerBypass": "Cuenta del propietario — la facturación está omitida para pruebas.",
  "settings.plan.nextChargeNoPlan": "Sin cargo próximo — elija un plan para crear cotizaciones.",
  "settings.plan.nextChargeTrialHeadline": "Primer cargo tras la prueba el {{date}}.",
  "settings.plan.nextChargeRenewalHeadline": "Renueva el {{date}}.",
  "settings.plan.nextChargeDateOnly": "Próxima fecha de facturación: {{date}}.",
  "settings.plan.nextChargeUnknown": "La fecha aparecerá cuando su suscripción esté activa.",
  "settings.plan.billingIntervalAnnual": "/año",
  "settings.plan.billingIntervalMonthly": "/mes",
  "settings.plan.billingCycleMonthly": "Facturación mensual",
  "settings.plan.billingCycleAnnual": "Facturación anual",
  "settings.plan.ownerManagesBilling": "El propietario de la cuenta gestiona la facturación.",
  "settings.plan.ownerManagesBillingShort": "El propietario gestiona la facturación",
  "settings.plan.scheduledChange": "Cambio programado: pasa a {{tier}} el {{date}}.",
  "settings.plan.statusTrialing": "Prueba gratis",
  "settings.plan.statusActive": "Activo",
  "settings.plan.statusPastDue": "Vencido",
  "settings.plan.statusCanceled": "Cancelado",
  "settings.plan.statusInactive": "Inactivo",
  "settings.plan.unlimited": "Ilimitado",
  "settings.plan.shortDesc.starter":
    "Para empezar: cotizaciones enviadas ilimitadas y el flujo básico para un solo propietario.",
  "settings.plan.shortDesc.solo":
    "Para un solo propietario: cotizaciones enviadas ilimitadas (correo, PDF o enlace) y el flujo completo de cotización y trabajos.",
  "settings.plan.shortDesc.pro":
    "Para equipos pequeños: redacción con IA, asistente de cotización, revisión IA, captura por voz/foto, calendario de cuadrilla, análisis y cotizaciones enviadas ilimitadas.",
  "settings.plan.shortDesc.agency":
    "Para operaciones mayores: envíos ilimitados (correo, PDF o enlace), su marca en el portal, soporte prioritario y coach de IA.",

  "settings.payout.title": "Cuenta de pagos",
  "settings.payout.description":
    "Conecte la cuenta que recibe los pagos con tarjeta en línea desde los checkouts de cotizaciones. Puede enviar cotizaciones por correo o PDF sin esto—conéctela cuando quiera cobrar con tarjeta en el enlace al cliente.",
  "settings.payout.connected":
    "Su configuración de pagos está conectada con Stripe. Cuando Stripe termine la verificación, los pagos con tarjeta en el enlace pueden depositarse ahí.",
  "settings.payout.notConnected":
    "Aún no está conectada—no pasa nada. Puede enviar cotizaciones por correo o PDF. Conecte aquí cuando quiera cobro con tarjeta en la página pública.",
  "settings.payout.verified": "Verificado y listo. Los pagos con tarjeta de los checkouts se depositarán directamente en su banco.",
  "settings.payout.pending": "La configuración de pagos no está completa todavía. Termina la configuración para empezar a aceptar pagos con tarjeta en los enlaces de cotización.",
  "settings.payout.finishSetup": "Terminar configuración",
  "settings.payout.finishSetupHint": "Complete la verificación y agregue su cuenta bancaria en Stripe",
  "settings.payout.verifying": "Stripe está revisando su información",
  "settings.payout.verifyingHint":
    "Esto suele tardar unos minutos, pero en algunos casos puede llegar a 1–2 días hábiles (lo más común es cuando la cuenta bancaria se agrega por micro‑depósito). No tiene que hacer nada más — le enviaremos un correo en cuanto los pagos con tarjeta estén activos.",
  "settings.payout.connect": "Conectar cuenta de pagos",
  "settings.payout.connectHint": "Configure su cuenta bancaria para recibir pagos con tarjeta en los enlaces de cotización",
  "settings.payout.updateSetup": "Actualizar configuración de pagos",
  "settings.payout.updateSetupHint": "Cambie su cuenta bancaria o actualice la información de verificación en Stripe",
  "settings.payout.openDashboard": "Ver pagos y saldo",
  "settings.payout.openDashboardHint": "Vea depósitos, historial y programación de pagos en Stripe",
  "settings.payout.banner.verifiedTitle": "Configuración de pagos completa",
  "settings.payout.banner.verifiedBody":
    "Ya está listo: los clientes pueden pagar con tarjeta en los enlaces de cotización y los depósitos llegarán a su banco.",
  "settings.payout.banner.verifyingTitle": "Listo, Stripe está revisando su información",
  "settings.payout.banner.verifyingBody":
    "Suele ser unos minutos, pero en algunos casos puede llegar a 1–2 días hábiles. Le enviaremos un correo en cuanto los pagos con tarjeta estén activos — no necesita volver a verificar.",
  "settings.payout.banner.incompleteTitle": "La configuración no se completó",
  "settings.payout.banner.incompleteBody":
    "Parece que Stripe no recibió toda la información necesaria. Haga clic en \"Terminar configuración\" abajo para continuar donde lo dejó — su progreso está guardado.",
  "settings.payout.banner.errorTitle": "No pudimos comunicarnos con Stripe",
  "settings.payout.banner.errorBody":
    "No pudimos confirmar su estado con Stripe en este momento. Pulse \"Verificar estado ahora\" en un minuto, o recargue la página.",
  "settings.payout.footer":
    "Los datos bancarios permanecen en Stripe. Jorvio Quotes no guarda números de cuenta ni de ruta en esta app. El envío por correo y PDF no requiere cuenta de pagos.",
  "settings.payout.depositTimingNote":
    "Según el calendario estándar de Stripe, los fondos se depositan en su banco unos 2 días hábiles después de cada pago aprobado. El primer depósito puede tardar de 7 a 14 días mientras Stripe verifica la nueva cuenta.",

  "settings.archive.title": "Almacenamiento de cotizaciones",
  "settings.archive.description": "Gestione las cotizaciones archivadas.",
  "settings.archive.button": "Ver cotizaciones archivadas",
  "settings.archivedPageTitle": "Cotizaciones y trabajos archivados",
  "settings.archivedPageSubtitle":
    "Las cotizaciones archivadas no aparecen en la lista principal. Los trabajos archivados no aparecen en Activos.",
  "settings.archivedQuotesSection": "Cotizaciones archivadas",
  "settings.archivedJobsSection": "Trabajos archivados",
  "settings.archivedJobsEmpty": "No hay trabajos archivados.",
  "settings.archivedJobRestore": "Restaurar trabajo",
  "settings.archivedQuotesEmptyTitle": "No hay cotizaciones archivadas",
  "settings.archivedQuotesEmptyDesc": "Las cotizaciones archivadas aparecerán aquí.",
  "settings.archivedDeleteQuoteAria": "Eliminar cotización archivada",

  "settings.followup.title": "Automatización de seguimiento",
  "settings.followup.description":
    "Cree una secuencia de recordatorios por defecto que se ejecute tras enviar una cotización por primera vez.",
  "settings.followup.agencyIncluded": "Agency incluye automatización de seguimiento personalizable para cotizaciones enviadas.",
  "settings.followup.agencyAvailable": "La automatización de seguimiento personalizada está disponible en el plan Agency.",
  "settings.followup.manage": "Gestionar automatización",
  "settings.followup.viewFeature": "Ver función Agency",

  "settings.owner.title": "Panel del propietario",
  "settings.owner.description":
    "Métricas internas, analítica de suscripciones y controles de prueba para su propia cuenta.",
  "settings.owner.open": "Abrir panel del propietario",

  "settings.crew.title": "Acceso al calendario del equipo",
  "settings.crew.description":
    "Comparta un código de equipo para que el personal abra un calendario de solo lectura con los trabajos asignados. Inician sesión desde el login con «Introducir código de equipo» — sin correo. Los nombres aparecen aquí y en «Asignar a» en cotizaciones y el calendario.",
  "settings.crew.hintRotate":
    "Quien tenga el código puede iniciar el registro del equipo. Crear un código nuevo invalida el anterior solo para quienes se unan a partir de ahora; no quita el acceso a quien ya usaba un código anterior.",
  "settings.crew.noCodeYet": "Aún no hay código de equipo. Genere uno para habilitar el acceso.",
  "settings.crew.activeCodeLabel": "Su código de equipo activo",
  "settings.crew.copyCode": "Copiar código",
  "settings.crew.codeHiddenTitle": "Hay un código activo, pero no está guardado para mostrarlo.",
  "settings.crew.codeHiddenBody":
    "Genere uno nuevo abajo para verlo y compartirlo. Eso invalida el código anterior solo para nuevos registros; comparta el nuevo con quien se una de aquí en adelante.",
  "settings.crew.generating": "Generando…",
  "settings.crew.generateNew": "Generar nuevo código de equipo",
  "settings.crew.generateFirst": "Generar código de equipo",
  "settings.crew.refreshRoster": "Actualizar lista del equipo",
  "settings.crew.rosterTitle": "Miembros del equipo (calendario)",
  "settings.crew.rosterHint":
    "Personas que abrieron su calendario de equipo al menos una vez. Use estos nombres al asignar trabajos.",
  "settings.crew.noSignins":
    "Aún no hay accesos. Comparta el código desde el login; la lista se actualiza al abrir el calendario.",
  "settings.crew.tableName": "Nombre",
  "settings.crew.tableLastActive": "Última actividad",
  "settings.crew.tableVisits": "Visitas",
  "settings.crew.toastCodeFail": "No se pudo actualizar el código",
  "settings.crew.toastCodeUpdatedTitle": "Código de equipo actualizado",
  "settings.crew.toastCodeUpdatedDesc":
    "Copie el código nuevo para personal nuevo. El código anterior ya no sirve para nuevos registros; quien ya está en el equipo conserva el acceso.",

  "settings.cooperator.title": "Acceso co-operador",
  "settings.cooperator.description":
    "Persona de confianza: mismos permisos de cotizaciones y calendario que usted, salvo borrar la cuenta de la empresa. Código distinto al del calendario de equipo.",
  "settings.cooperator.hint":
    "Se registran en Unirse con código tras compartir la invitación. El código de calendario es solo para el calendario de campo.",
  "settings.cooperator.noCodeYet": "Aún no hay código de co-operador. Genérelo para invitar.",
  "settings.cooperator.activeCodeLabel": "Código de invitación co-operador activo",
  "settings.cooperator.copyCode": "Copiar código",
  "settings.cooperator.codeHiddenTitle": "Hay un código activo pero no está guardado para mostrarlo.",
  "settings.cooperator.codeHiddenBody":
    "Genere uno nuevo para verlo y compartirlo. Eso invalida la invitación anterior solo para nuevos registros; quien ya se unió conserva el acceso.",
  "settings.cooperator.generating": "Generando…",
  "settings.cooperator.generateNew": "Nuevo código de co-operador",
  "settings.cooperator.generateFirst": "Generar código de co-operador",
  "settings.cooperator.toastCodeFail": "No se pudo actualizar el código",
  "settings.cooperator.toastCodeUpdatedTitle": "Código de co-operador actualizado",
  "settings.cooperator.toastCodeUpdatedDesc":
    "Comparta el código nuevo para nuevos co-operadores. La invitación anterior ya no sirve para nuevos registros; quien ya se unió conserva el acceso.",

  "settings.crewInbox.title": "Mensajes del equipo",
  "settings.crewInbox.subtitle":
    "Chatee con quien inició sesión con su código de equipo. Ven este hilo en su página de calendario tras unirse.",
  "settings.crewInbox.emptyList":
    "Aún no hay conversaciones. Cuando alguien use su código, aparecerá arriba. Luego podrá escribirle aquí.",
  "settings.crewInbox.teamMember": "Miembro del equipo",
  "settings.crewInbox.pickPerson": "Elija una persona",
  "settings.crewInbox.selectPrompt": "Seleccione alguien para ver mensajes o iniciar un hilo.",
  "settings.crewInbox.loadingMsgs": "Cargando mensajes…",
  "settings.crewInbox.noMsgs": "Aún no hay mensajes. Salude abajo.",
  "settings.crewInbox.office": "Oficina",
  "settings.crewInbox.crew": "Equipo",
  "settings.crewInbox.writePlaceholder": "Escriba un mensaje…",
  "settings.crewInbox.sending": "Enviando…",
  "settings.crewInbox.send": "Enviar",
  "settings.crewInbox.refreshList": "Actualizar lista",
  "settings.crewInbox.toastLoadFail": "No se pudo cargar la bandeja del equipo",
  "settings.crewInbox.toastLoadMsgsFail": "No se pudieron cargar los mensajes",
  "settings.crewInbox.toastChooseMember": "Elija un miembro del equipo e introduzca un mensaje",
  "settings.crewInbox.toastSendFail": "Error al enviar",
  "settings.crewInbox.toastSent": "Enviado",

  "settings.logo.title": "Logotipo",
  "settings.logo.description": "Se muestra en cotizaciones al cliente. Mejor imagen cuadrada.",
  "settings.section.personal.title": "Perfil personal",
  "settings.section.personal.description": "Idioma, nombre, correo de acceso, teléfono personal y contraseña.",
  "settings.section.business.title": "Datos del negocio",
  "settings.section.business.description":
    "Identidad, oficio, marca y dirección que aparecen con los datos del negocio en cotizaciones al cliente cuando aplique.",
  "settings.profile.personalPhone": "Teléfono personal",
  "settings.profile.personalPhoneHelp":
    "Solo para sus registros — no aparece en cotizaciones al cliente. La app no envía SMS ni verifica números.",
  "settings.profile.quoteContactEmail": "Correo de contacto en cotizaciones",
  "settings.profile.quoteContactEmailHelp":
    "Se muestra con los datos del negocio en PDF y vista previa. Vacío = correo de acceso.",
  "settings.profile.phoneOnQuotesHelp": "Se muestra con los datos del negocio en PDF y vista previa.",
  "settings.profile.requiredFieldMarker": "Obligatorio",
  "settings.validation.quoteEmail": "Introduzca un correo válido o déjelo en blanco.",
  "settings.validation.vanitySubdomain": "Use solo letras, números o guiones (sin espacios).",
  "settings.nav.personal": "Personal",
  "settings.nav.business": "Empresa",
  "settings.nav.logos": "Logotipos",
  "settings.nav.ai": "IA y textos",
  "settings.nav.integrations": "Integraciones",
  "settings.nav.plan": "Plan",
  "settings.nav.payout": "Pagos",
  "settings.nav.archive": "Archivo",
  "settings.nav.priceBook": "Lista de precios",
  "settings.nav.ops": "Operaciones",
  "settings.nav.followUp": "Seguimiento",
  "settings.nav.owner": "Propietario",
  "settings.nav.crew": "Equipo",
  "settings.nav.teamCode": "Código equipo",
  "settings.nav.team": "Personas",
  "settings.nav.legal": "Legal",
  "settings.nav.account": "Cuenta",
  "settings.nav.group.profileCompany": "Perfil y empresa",
  "settings.nav.group.billingWorkflow": "Facturación y flujo",
  "settings.nav.group.team": "Equipo",
  "settings.nav.group.account": "Cuenta",
  "settings.nav.group.owner": "Propietario",
  "settings.tab.personal": "Perfil personal",
  "settings.tab.company": "Datos del negocio",
  "settings.tab.reminders": "Recordatorios inteligentes",
  "settings.tab.brand": "Logotipos",
  "settings.tab.aiQuotes": "IA de cotización",
  "settings.tab.reviews": "Reseñas",
  "settings.tab.plan": "Plan y facturación",
  "settings.tab.payments": "Pagos",
  "settings.tab.workspace": "Archivo y herramientas",
  "settings.tab.access": "Códigos de acceso",
  "settings.tab.people": "Personas y lista",
  "settings.tab.account": "Cuenta y soporte",
  "settings.tab.owner": "Panel del propietario",

  "settings.profile.title": "Perfil",
  "settings.profile.description":
    "Lo que aparece en las cotizaciones al cliente: identidad de empresa, marca y contacto.",
  "settings.profile.yourName": "Su nombre",
  "settings.profile.companyName": "Nombre de la empresa",
  "settings.profile.phone": "Teléfono",
  "settings.profile.trade": "Oficio",
  "settings.profile.selectTrade": "Seleccione oficio",
  "settings.profile.tradeCustom": "Su oficio o especialidad",
  "settings.profile.tradeCustomPlaceholder": "p. ej. piscinas, electrodomésticos, mantenimiento",
  "settings.profile.brandColor": "Color de marca",
  "settings.profile.licenseNumber": "Número de licencia",
  "settings.profile.vanityQuoteSubdomain": "Subdominio del enlace de cotización personalizado (Agency)",
  "settings.profile.vanityQuoteSubdomainHelp":
    "Defina un subdominio solo para Agency para personalizar los enlaces de cotizaciones del cliente (p. ej. acmeelectric -> https://acmeelectric.jorvioquotes.com/p/<token>).",
  "settings.profile.address": "Dirección",
  "settings.profile.city": "Ciudad",
  "settings.profile.state": "Estado / provincia",
  "settings.profile.emailLabel": "Correo electrónico",
  "settings.profile.emailCurrent": "Correo de la cuenta actual:",
  "settings.profile.emailUpdate": "Actualizar correo",
  "settings.profile.emailSending": "Enviando…",
  "settings.profile.emailHelp": "Su nuevo correo se activa solo después de hacer clic en el enlace de confirmación que enviaremos.",
  "settings.profile.emailEnter": "Ingrese un correo",
  "settings.profile.emailUpdateFailed": "No se pudo actualizar el correo",
  "settings.profile.emailConfirmationSentTitle": "Correo de confirmación enviado",
  "settings.profile.emailConfirmationSentDesc": "Haga clic en el enlace enviado a {{email}} para cambiar el correo de su cuenta.",
  "settings.profile.emailPendingTitle": "Correo de confirmación enviado a {{email}}.",
  "settings.profile.emailPendingBody": "Haga clic en el enlace en esa bandeja para cambiar el correo de su cuenta. Su correo actual sigue activo hasta entonces.",
  "settings.profile.emailPendingResend": "Reenviar",
  "settings.profile.emailPendingDismiss": "Cerrar",
  "settings.profile.zip": "Código postal",

  "settings.trade.hvac": "Climatización",
  "settings.trade.plumbing": "Fontanería",
  "settings.trade.electrical": "Electricidad",
  "settings.trade.roofing": "Techos",
  "settings.trade.landscaping": "Jardinería",
  "settings.trade.painting": "Pintura",
  "settings.trade.general": "Contratista general",
  "settings.trade.other": "Otro",

  "settings.business.title": "Instrucciones y valores por defecto de la IA",
  "settings.business.description":
    "Un solo lugar para decirle a la IA cómo redactar tus cotizaciones y qué información estándar incluir en ellas.",
  "settings.business.reuseLabel": "Texto para reutilizar en cotizaciones",
  "settings.business.reusePlaceholder":
    "p. ej. «Visita / desplazamiento: 89 $, exento si aprueba el trabajo.» Depósito 50 % en trabajos > 1.000 $.",
  "settings.business.reuseHelp":
    "Opcional. Se guarda aquí para no volver a escribirlo. Añádelo a una cotización y aparecerá en la que ve tu cliente.",
  "settings.business.aiLabel": "Instrucciones e información estándar para la IA",
  "settings.business.aiPlaceholder":
    "p. ej. «Siempre 40 % de depósito por adelantado. Mencionar nuestra garantía de un año. Visita 89 $, exenta si se aprueba el trabajo.»",
  "settings.business.aiHelp":
    "Opcional. Una sola casilla para ambas cosas: reglas de cómo la IA redacta tus cotizaciones (tono, formato) e información estándar para incluir (% de depósito, tarifas, garantía, políticas). La IA sigue las reglas y añade lo visible para el cliente en cada cotización.",

  "settings.validation.tradeCustom": "Describa su oficio o especialidad",

  "quotes.title": "Cotizaciones",
  "quotes.subtitle": "Todas las cotizaciones",
  "quotes.searchPlaceholder": "Buscar en título o resumen…",
  "quotes.searchAria": "Buscar cotizaciones",
  "quotes.sort.placeholder": "Ordenar por",
  "quotes.sortAria": "Ordenar cotizaciones",
  "quotes.sort.updated_desc": "Última edición",
  "quotes.sort.created_desc": "Creadas recientemente",
  "quotes.sort.sent_desc": "Enviadas recientemente",
  "quotes.sort.total_desc": "Total más alto",
  "quotes.sort.title_asc": "Título A–Z",
  "quotes.statusFilterPlaceholder": "Estado",
  "quotes.statusFilterAria": "Filtrar por estado",
  "quotes.status.all": "Todos los estados",
  "quotes.status.draft": "Borrador",
  "quotes.status.sent": "Enviada",
  "quotes.status.viewed": "Vista",
  "quotes.status.viewedStalled": "Vista, sin decisión (4+ días)",
  "quotes.status.accepted": "Aceptada",
  "quotes.status.declined": "Rechazada",
  "quotes.status.expired": "Vencida",
  "quotes.paymentBadge.paid": "Pagada",
  "quotes.paymentBadge.depositPaid": "Anticipo recibido",
  "quotes.paymentBadge.refunded": "Reembolsada",
  "quotes.newQuote": "Nueva cotización",
  "quotes.loadErrorTitle": "No se pudieron cargar las cotizaciones",
  "quotes.loadErrorDesc": "Error en la solicitud",
  "quotes.emptyTitle": "Aún no hay cotizaciones",
  "quotes.emptyDesc":
    "Cree una cotización con partidas por IA y envíe un enlace que el cliente abra en cualquier dispositivo.",
  "quotes.addCustomerFirst": "Añadir un cliente primero",
  "quotes.noMatchTitle": "No hay cotizaciones coincidentes",
  "quotes.noMatchDesc": "Pruebe otra búsqueda, estado, rango de fechas u opción de orden.",
  "quotes.clearFilters": "Borrar filtros",
  "quotes.loadMore": "Cargar más",
  "quotes.loadingMore": "Cargando más…",
  "quotes.shownOfTotal": "Mostrando {shown} de {total}",
  "quotes.dateUpdatedFrom": "Editada desde",
  "quotes.dateUpdatedTo": "Editada hasta",
  "quotes.dateRangeHint": "Filtrar por última edición (zona horaria local).",
  "quotes.priceBookSearchPlaceholder": "Buscar en catálogo…",
  "quotes.card.customer": "Cliente",
  "quotes.card.validUntil": "Válida hasta",
  "quotes.card.total": "Total",
  "quotes.card.updated": "Actualizada",
  "quotes.card.archiveTitle": "Archivar cotización",
  "quotes.card.unarchiveTitle": "Restaurar cotización",
  "quotes.card.unarchive": "Restaurar",
  "quotes.card.copyLink": "Copiar enlace",
  "quotes.card.quickNote": "Nota rápida",
  "quotes.card.quickNoteTitle": "Nota en la lista",
  "quotes.card.quickNotePlaceholder": "Una línea para su equipo (no visible para el cliente)",
  "quotes.card.quickNoteSave": "Guardar",
  "quotes.card.quickNoteSaved": "Nota guardada",
  "quotes.card.quickNoteAddTitle": "Añadir nota rápida",
  "quotes.card.quickNoteDialogTitle": "Añadir nota rápida",
  "quotes.card.expiredBadge": "Vencida",
  "quotes.card.expiresTodayBadge": "Vence hoy",
  "quotes.card.expiresInDaysBadge": "Vence en {{days}} días",
  "quotes.card.expiresInOneDayBadge": "Vence en 1 día",
  "quotes.card.notOpenedYet": "Aún sin abrir (sin vistas)",
  "quotes.card.movedToJobs": "Movido a trabajos",
  "quotes.card.movedToJobsTitle": "Este presupuesto fue aceptado y ahora se gestiona en Trabajos",
  "quotes.card.movedToJobsNote": "Aceptado… ahora aparecerá en Trabajos.",
  "quotes.card.openedTimes": "Abierta {{count}}×",
  "quotes.card.openedLast": "Última {{date}}",
  "quotes.pricing.standardLabel": "Precio en cada línea",
  "quotes.pricing.marginTargetLabel": "Costo + margen %",
  "quotes.pricing.standardTooltip":
    "El precio de cada línea es lo que paga el cliente. El asistente puede sugerir tarifas típicas para su oficio.",
  "quotes.pricing.marginTargetTooltip":
    "Ingrese su costo en cada línea. El total del cliente suma su porcentaje de margen.",
  "quotes.toast.archiveFailed": "Error al archivar",
  "quotes.toast.archived": "Cotización archivada",
  "quotes.toast.undo": "Deshacer",
  "quotes.toast.undoArchiveAlt": "Volver a poner esta cotización en la lista activa",
  "quotes.toast.unarchiveFailed": "Error al restaurar",
  "quotes.toast.restored": "Cotización restaurada",

  "dashboard.subtitle": "Resumen de cotizaciones, pipeline y cobros",
  "dashboard.scheduleNudgeTitle": "La ventana de trabajo termina pronto",
  "dashboard.scheduleNudgeIntro":
    "Estos trabajos aceptados terminan en obra en unos dos días (o ya terminaron). Actualice el calendario en la cotización si necesita más tiempo, o recuerde al cliente el saldo.",
  "dashboard.scheduleNudgeEnd": "Fin programado",
  "dashboard.scheduleNudgeBalance": "Saldo pendiente (estimado)",
  "dashboard.scheduleNudgeOpenQuote": "Abrir cotización",
  "dashboard.scheduleNudgeBalanceEmail": "Correo de saldo",
  "dashboard.recentQuotes": "Cotizaciones recientes",
  "dashboard.recent.empty": "Aún no hay cotizaciones.",
  "dashboard.recent.createFirst": "Cree su primera cotización",
  "dashboard.recent.viewAll": "Ver todas",
  "dashboard.recent.colQuote": "Cotización",
  "dashboard.recent.colCustomer": "Cliente",
  "dashboard.recent.colStatus": "Estado",
  "dashboard.recent.colAmount": "Importe",
  "dashboard.recent.colUpdated": "Actualizada",
  "dashboard.stats.summary": "{{total}} cotizaciones · {{accepted}} aceptadas · {{winRate}} tasa de cierre",
  "dashboard.stats.totalQuotes": "Total de cotizaciones",
  "dashboard.stats.totalQuotesHint": "Cotizaciones activas (excluye archivadas).",
  "dashboard.stats.quotesPending": "Cotizaciones pendientes",
  "dashboard.stats.quotesPendingHint":
    "Enviada o vista: aún no aceptada, rechazada ni vencida. Sin borradores.",
  "dashboard.stats.totalProfit": "Beneficio total",
  "dashboard.stats.totalProfitHint":
    "Lo que paga el cliente en cotizaciones aceptadas, menos sus costes. Incluye trabajos aceptados aunque le hayan pagado fuera de la app.",
  "dashboard.stats.totalProfitHintStarter":
    "Lo que paga el cliente en cotizaciones aceptadas, menos los costes que registró en esas cotizaciones.",
  "dashboard.stats.jobsCompleted": "Trabajos completados",
  "dashboard.stats.jobsCompletedHint": "Trabajos marcados como completados en Trabajos.",
  "dashboard.stats.quoteWinRate": "Tasa de cierre",
  "dashboard.stats.quoteWinRateHint": "Cotizaciones aceptadas entre todas las que ha enviado.",
  "dashboard.stats.profitLast30": "Beneficio de cotizaciones aceptadas (últimos 30 días)",
  "dashboard.stats.profitLast30Hint":
    "Beneficio el día en que se aceptó cada cotización. Misma fórmula que el beneficio total, incluidos trabajos pagados fuera de la app.",
  "dashboard.stats.profitLast30HintStarter":
    "Beneficio el día en que se aceptó cada cotización. Misma fórmula que el beneficio total: total menos sus costes.",
  "dashboard.stats.profitChartEmptyTitle": "Aún no hay cotizaciones aceptadas",
  "dashboard.stats.profitChartEmptyBody":
    "El beneficio diario aparece cuando los clientes aceptan. Basado en el total menos sus costes internos.",
  "dashboard.stats.profitChartOlderTitle": "Sin cotizaciones aceptadas en los últimos 30 días",
  "dashboard.stats.profitChartOlderBody":
    "Su beneficio total de arriba incluye cotizaciones aceptadas anteriores. Este gráfico solo cubre los últimos 30 días, por lo que permanece plano hasta que se acepte una nueva cotización.",
  "dashboard.stats.showHint": "Mostrar explicación",
  "dashboard.stats.hideHint": "Ocultar explicación",
  "dashboard.onboarding.welcomeTitle": "Bienvenido — saquemos su primera cotización",
  "dashboard.onboarding.welcomeDesc":
    "Cuando envíe cotizaciones, las estadísticas aquí (enviadas, vistas, ganadas, cotizaciones abiertas) se actualizan solas.",
  "dashboard.onboarding.step1Title": "Añadir un cliente",
  "dashboard.onboarding.step1Body": "Guarde los datos de contacto para trabajos recurrentes.",
  "dashboard.onboarding.step2Title": "Cree su primera cotización",
  "dashboard.onboarding.step2Body": "Genere partidas con IA, luego edite y envíe.",
  "dashboard.onboarding.step3Title": "Envíe el enlace",
  "dashboard.onboarding.step3Body": "Los clientes abren la cotización en el móvil y pueden aceptar con firma.",
  "dashboard.onboarding.go": "Ir →",
  "dashboard.onboarding.customers": "Clientes",
  "dashboard.onboarding.activityTitle": "Vista previa de actividad",
  "dashboard.onboarding.activityDesc": "Después de enviar cotizaciones, aparecen tendencias aquí.",
  "dashboard.onboarding.bullet1": "Conteos de enviadas y vistas por mes",
  "dashboard.onboarding.bullet2": "Totales de cotizaciones abiertas e ingresos ganados en aceptadas",
  "dashboard.team.title": "Calendario del equipo",
  "dashboard.team.description":
    "En cotizaciones o trabajos, use Asignar a para indicar quién hará la obra. Cada persona solo ve en el calendario de cuadrilla (/crew) los trabajos asignados a ella, después de iniciar sesión con el código del equipo.",
  "dashboard.team.calendarCode": "Ver código del equipo",
  "dashboard.team.crewAccessCodeHelp":
    "Abra el botón para ver o copiar el código. El personal va a /crew, ingresa el código una vez, inicia sesión y usa el mismo nombre que usted elija en Asignar a.",
  "dashboard.team.codeHint":
    "Cree un código de equipo en Ajustes → Equipo cuando quiera que vean sus asignaciones en el calendario.",
  "dashboard.team.sharingOn": "Calendario de equipo activado",
  "dashboard.team.since": "Código del equipo activo desde {{date}}",
  "dashboard.team.teamCodeActive": "El código de equipo está activo.",
  "dashboard.team.signinsNone":
    "Aún no hay accesos del equipo. Comparta el código y pida que cada persona abra /crew e inicie sesión con el mismo nombre que use en Asignar a.",
  "dashboard.team.signinsOne":
    "1 persona inició sesión en /crew — revise el nombre que ingresó abajo.",
  "dashboard.team.signinsMany":
    "{{count}} personas iniciaron sesión en /crew — revise los nombres que ingresaron abajo.",
  "dashboard.team.colName": "Nombre que ingresaron",
  "dashboard.team.colVisits": "Visitas",
  "dashboard.team.colLastOpened": "Última apertura",

  ...EXTENDED_ES,
};

const fr: MessageDict = {
  ...en,
  "nav.dashboard": "Tableau de bord",
  "nav.calendar": "Calendrier",
  "nav.quotes": "Devis",
  "nav.newQuote": "Nouveau devis",
  "nav.customers": "Clients",
  "nav.templates": "Modèles",
  "nav.settings": "Paramètres",
  "nav.home": "Accueil",
  "nav.new": "Nouveau",
  "nav.clients": "Clients",
  "nav.more": "Plus",
  "nav.billing": "Facturation",
  "nav.help": "Aide",
  "nav.primary": "Principal",
  "billingGate.subtitle": "Choisissez un plan avant de créer votre premier devis.",
  "billingGate.cardTitle": "Un plan est requis",
  "billingGate.cardDesc":
    "Votre essai gratuit est terminé. Ajoutez un plan pour continuer à créer et envoyer des devis. Vos devis et clients existants sont conservés.",
  "billingGate.browseNote":
    "Vous pouvez toujours parcourir l'application, mais la création de devis nécessite un plan actif après l'essai.",
  "billingGate.choosePlan": "Choisir un plan",
  "billingGate.backToDashboard": "Retour au tableau de bord",
  "billingBanner.platformTrial.one":
    "Il vous reste 1 jour d'essai gratuit. Choisissez un plan et ajoutez votre carte avant la fin pour garder l'accès complet.",
  "billingBanner.platformTrial.many":
    "Il vous reste {days} jours d'essai gratuit. Choisissez un plan et ajoutez votre carte avant la fin pour garder l'accès complet.",
  "billingBanner.pickPlan": "Choisir un plan",
  "billingBanner.stripeTrialing.one":
    "Il vous reste 1 jour d'essai initial. Ajoutez un moyen de paiement avant la fin pour garder l'accès complet.",
  "billingBanner.stripeTrialing.many":
    "Il vous reste {days} jours d'essai initial. Ajoutez un moyen de paiement avant la fin pour garder l'accès complet.",
  "billingBanner.addPayment": "Ajouter un moyen de paiement",
  "billingBanner.billing": "Facturation",
  "billingBanner.ownerPromo.one":
    "Il vous reste environ 1 jour sur votre plan promotionnel gratuit. Ajoutez un moyen de paiement dans Facturation avant la fin pour garder l'accès complet.",
  "billingBanner.ownerPromo.many":
    "Il vous reste environ {days} jours sur votre plan promotionnel gratuit. Ajoutez un moyen de paiement dans Facturation avant la fin pour garder l'accès complet.",
  "billingBanner.promoPick":
    "Votre accès promotionnel est terminé. Choisissez un plan payant pour continuer à utiliser Jorvio. Nous recommandons {tier} (le niveau que vous aviez en promotion).",
  "billingBanner.choosePlan": "Choisir un plan",
  "billingBanner.trialEnded":
    "Votre essai gratuit est terminé. Votre espace de travail est en lecture seule. Choisissez un plan et ajoutez votre carte pour recommencer à créer, envoyer et enregistrer.",
  "billingBanner.paymentFailed":
    "Nous n'avons pas pu traiter votre dernier paiement. Votre espace de travail est en lecture seule jusqu'à la mise à jour de la carte. Vous pouvez toujours consulter les devis et les chantiers.",
  "billingBanner.updatePayment": "Mettre à jour le paiement",
  "a11y.skipToMain": "Aller au contenu principal",
  "nav.followUpsOpenQuotes": "Suivi et analyses",
  "nav.followUpsShort": "Suivi",
  "nav.messages": "Messages",
  "nav.jobs": "Chantiers",
  "jobs.title": "Chantiers",
  "jobs.subtitle":
    "Chaque chantier dans une liste, avec ses devis liés, son planning et le solde restant.",
  "jobs.unscheduled": "Non planifié",
  "jobs.noCustomerLinked": "Aucun client lié",
  "jobs.tab.active": "Actifs",
  "jobs.tab.completed": "Terminés",
  "jobs.tab.archived": "Archivés",
  "jobs.emptyTitle": "Pas encore de chantiers",
  "jobs.emptyDesc": "Créez un chantier manuellement ou acceptez un devis pour en générer un.",
  "jobs.newJob": "Nouveau chantier",
  "jobs.backDashboard": "Retour au tableau de bord",
  "jobs.backToList": "Tous les chantiers",
  "jobs.loadErrorTitle": "Impossible de charger les chantiers",
  "jobs.loadErrorDesc": "Réessayez dans un instant.",
  "jobs.scheduled": "Planifié",
  "jobs.quotesLinked": "Devis",
  "jobs.completedLabel": "Terminé",
  "jobs.openDetail": "Ouvrir →",
  "jobs.fieldTitle": "Titre du chantier",
  "jobs.fieldTitlePlaceholder": "ex. Rénovation cuisine Smith",
  "jobs.fieldStartOptional": "Début (facultatif)",
  "jobs.fieldEndOptional": "Fin (facultatif)",
  "jobs.fieldStartDate": "Date de début",
  "jobs.fieldEndDate": "Date de fin",
  "jobs.fieldTimeOptional": "Heure",
  "jobs.schedulingSectionTitle": "Planification (facultatif)",
  "jobs.scheduleDefaultsHint":
    "Choisissez la date de début et/ou de fin. Laissez l’heure vide pour 9:00 au début et 17:00 à la fin (heure locale). L’heure n’apparaît que si vous la saisissez.",
  "jobs.fieldSite": "Adresse du chantier",
  "jobs.fieldNotes": "Notes",
  "jobs.cancel": "Annuler",
  "jobs.create": "Créer",
  "jobs.saving": "Enregistrement…",
  "jobs.createNeedTitle": "Saisissez un titre.",
  "jobs.createFailed": "Impossible de créer le chantier",
  "jobs.createOk": "Chantier créé",
  "jobs.payment.paid": "Payé",
  "jobs.payment.partial": "Partiellement payé",
  "jobs.payment.unpaid": "Non payé",
  "jobs.status.completed": "Terminé",
  "jobs.status.scheduled": "Planifié",
  "jobs.status.notScheduled": "Non planifié",
  "jobs.photos.sectionTitle": "Photos avant et après",
  "jobs.photos.before": "Avant",
  "jobs.photos.after": "Après",
  "jobs.photos.none": "Pas encore de photos",
  "jobs.photos.hint": "Facultatif. Le client peut les voir sur son portail.",
  "jobs.photos.beforeLabel": "Photos avant",
  "jobs.photos.afterLabel": "Photos après",
  "jobs.photos.addBefore": "Ajouter avant",
  "jobs.photos.addAfter": "Ajouter après",
  "jobs.photos.remove": "Retirer",
  "jobs.photos.uploading": "Envoi…",
  "jobs.photos.markComplete": "Marquer terminé",
  "jobs.photos.markCompleteNoAfterTitle": "Marquer terminé sans photos après ?",
  "jobs.photos.markCompleteNoAfterBody":
    "Vous n’avez pas ajouté de photos après. Les clients apprécient souvent de voir le résultat final. Vous pouvez quand même marquer le chantier comme terminé.",
  "jobs.photos.markCompleteConfirm": "Marquer terminé quand même",
  "jobs.detail.loadError": "Impossible de charger le chantier",
  "jobs.detail.saveFailed": "Impossible d’enregistrer",
  "jobs.detail.readOnlyTitle": "Espace en lecture seule",
  "jobs.detail.readOnlyBody": "Ajoutez un forfait ou corrigez la facturation dans Paramètres → Facturation pour modifier à nouveau les chantiers.",
  "jobs.detail.saved": "Enregistré",
  "jobs.detail.actionFailed": "Impossible de mettre à jour",
  "jobs.detail.attachFailed": "Impossible de lier le devis",
  "jobs.detail.attached": "Devis lié",
  "jobs.detail.detached": "Devis délié",
  "jobs.detail.deleteConfirm": "Supprimer ce chantier ? Les liens calendrier seront retirés.",
  "jobs.detail.deleteFailed": "Impossible de supprimer",
  "jobs.detail.deleted": "Chantier supprimé",
  "jobs.detail.notFound": "Chantier introuvable.",
  "jobs.detail.edit": "Modifier les détails",
  "jobs.detail.markIncomplete": "Marquer non terminé",
  "jobs.detail.markComplete": "Marquer terminé",
  "jobs.detail.unarchive": "Restaurer",
  "jobs.detail.archive": "Archiver",
  "jobs.detail.delete": "Supprimer",
  "jobs.detail.noSite": "Pas d’adresse",
  "jobs.detail.paymentTitle": "Paiements (devis liés)",
  "jobs.detail.percentPaid": "{{pct}} % payé",
  "jobs.detail.ofDue": "sur",
  "jobs.detail.scheduleTitle": "Planning et notes",
  "jobs.detail.scheduleHint": "Affiché sur votre calendrier lorsque des dates sont définies.",
  "jobs.detail.scheduleDayPickerTitle": "Planning du chantier",
  "jobs.detail.scheduleDayPickerHint":
    "Cliquez sur un jour pour l’ajouter ou le retirer. Les jours choisis se synchronisent avec le calendrier de l’entreprise (9 h–17 h, heure locale). Enregistrement automatique.",
  "jobs.detail.scheduleDayPickerSaving": "Enregistrement…",
  "jobs.detail.scheduleRangeTitle": "Plage de dates et heures",
  "jobs.detail.scheduleStartDate": "Date de début",
  "jobs.detail.scheduleEndDate": "Date de fin",
  "jobs.detail.scheduleStartTime": "Heure de début",
  "jobs.detail.scheduleEndTime": "Heure de fin",
  "jobs.detail.scheduleRepeatDays": "Répéter les jours",
  "jobs.detail.excludeWeekends": "Exclure les week-ends",
  "jobs.detail.weekendDisabledHint": "Désactivez « Exclure les week-ends » pour planifier le samedi ou le dimanche.",
  "jobs.detail.scheduleRepeatHint":
    "Laissez tous les boutons de jour désactivés pour compter chaque jour civil entre la date de début et la date de fin. Activez seulement les jours où le travail doit se répéter dans cette plage (par ex. mardi et jeudi). « Exclure les week-ends » signifie du lundi au vendredi.",
  "jobs.detail.configureSchedule": "Configurer le planning",
  "jobs.detail.configureScheduleDialogDesc":
    "Ajoutez les personnes sur ce chantier et définissez les jours de travail de chacune. Cliquez sur les jours du calendrier pour les activer ou les désactiver. Usage interne uniquement : rien de visible côté client.",
  "jobs.detail.scheduleCardTeaser":
    "Ouvrez Configurer le planning pour ajouter des membres et définir les jours de travail de chacun. Interne uniquement : le client ne le voit pas. Les notes restent sur cette page.",
  "jobs.detail.scheduleCalendarTitle": "Calendrier de travail",
  "jobs.detail.scheduleCalendarNeedDates":
    "Indiquez d’abord la date de début (et la date de fin si le chantier dure plus d’un jour). Vous pourrez ensuite activer ou désactiver des jours dans la grille.",
  "jobs.detail.schedulePrevMonth": "Mois précédent",
  "jobs.detail.scheduleNextMonth": "Mois suivant",
  "jobs.detail.scheduleSaveAndClose": "Enregistrer et fermer",
  "jobs.detail.clearSchedule": "Effacer le planning",
  "jobs.detail.notesPlaceholder": "Accès, stationnement, matériaux, préférences du client. Visible par votre équipe sur ce chantier.",
  "jobs.detail.clickToAddSite": "Cliquez pour ajouter l’adresse du chantier",
  "jobs.detail.editDialogHint":
    "Utilisez Planning et notes sur cette page pour le calendrier de chantier. Ici vous pouvez modifier le titre, l’adresse du site et les notes.",
  "jobs.detail.starts": "Début",
  "jobs.detail.ends": "Fin",
  "jobs.detail.noStart": "Pas de date de début — modifiez pour en ajouter.",
  "jobs.detail.invalidScheduleStart": "Date ou heure de début invalide. Corrigez la date ou laissez l’heure vide (9:00).",
  "jobs.detail.invalidScheduleEnd": "Date ou heure de fin invalide. Corrigez la date ou laissez l’heure vide (17:00).",
  "jobs.detail.notesLabel": "Notes",
  "jobs.detail.quotesTitle": "Devis liés",
  "jobs.detail.attachQuote": "Lier un devis",
  "jobs.detail.noQuotesToAttach": "Aucun devis accepté à lier.",
  "jobs.detail.pickQuote": "Devis",
  "jobs.detail.pickPlaceholder": "Choisir un devis",
  "jobs.detail.link": "Lier",
  "jobs.detail.noQuotes": "Aucun devis lié pour l’instant.",
  "jobs.detail.primary": "Principal",
  "jobs.detail.makePrimary": "Définir comme principal",
  "jobs.detail.detach": "Délier",
  "jobs.detail.save": "Enregistrer",
  "jobs.detail.paymentEdit": "Modifier manuellement les montants",
  "jobs.detail.paymentEditShort": "Modifier montants",
  "jobs.detail.paymentEditDesc":
    "Indiquez le montant payé et, si besoin, un montant dû personnalisé par devis. Laissez le montant dû vide pour utiliser le total du devis.",
  "jobs.detail.paymentFromQuote": "Selon le devis :",
  "jobs.detail.paymentAmountDue": "Montant dû",
  "jobs.detail.paymentAmountDueHint": "Vide = total du devis. Saisissez un nombre pour remplacer.",
  "jobs.detail.paymentAmountPaid": "Montant payé",
  "jobs.detail.paymentStripeNote":
    "Les paiements carte via Stripe mettent encore à jour le payé automatiquement ; ajustez ici pour espèces, chèques ou corrections.",
  "jobs.detail.paymentStripeFeeHint":
    "Ces totaux correspondent aux montants du devis (ce que vous gagnez). Si vous facturez les frais de carte au client, son lien de paiement peut afficher des montants un peu plus élevés — cette différence correspond aux frais Stripe, pas à des revenus manquants.",
  "jobs.detail.paymentStripePayoutHint":
    "Les paiements par carte mettent à jour le total payé ici tout de suite, mais Stripe verse les fonds sur votre compte bancaire selon son calendrier standard — en général environ 2 jours ouvrés après chaque paiement. Le premier versement peut prendre 7 à 14 jours, le temps que Stripe vérifie votre compte.",
  "jobs.detail.paymentStripePolicyLink": "Politique de versements Stripe",
  "jobs.detail.paymentHideStripeInfo": "Masquer",
  "jobs.detail.paymentShowStripeInfo": "Afficher les infos de paiement",
  "jobs.detail.paymentInvalidPaid": "Saisissez un montant payé valide (0 ou plus).",
  "jobs.detail.paymentInvalidDue": "Saisissez un montant dû valide ou laissez vide.",
  "jobs.detail.paymentSaveFailed": "Impossible d’enregistrer les montants",
  "jobs.detail.paymentSaved": "Montants enregistrés",
  "jobs.detail.requestBalancePayment": "Demander le solde restant",
  "jobs.detail.balanceReminderOpening": "Chargement…",
  "jobs.detail.balanceReminderTitle": "E-mail client — solde à payer",
  "jobs.detail.balanceReminderDesc":
    "Envoyez un rappel avec le même lien de paiement sécurisé que le devis d’origine.",
  "jobs.detail.balanceReminderEmailHelp":
    "Pour toute question sur le montant ou le calendrier, ouvrez le lien de votre devis et envoyez un message à votre entrepreneur.",
  "jobs.detail.balanceReminderSubjectLabel": "Objet",
  "jobs.detail.balanceReminderMessageLabel": "Message",
  "jobs.detail.balanceReminderSubjectPrefix": "Solde à payer",
  "jobs.detail.balanceReminderSend": "Envoyer l’e-mail",
  "jobs.detail.balanceReminderSending": "Envoi…",
  "jobs.detail.balanceReminderSent": "Rappel envoyé",
  "jobs.detail.balanceReminderSentDesc": "Le client recevra votre message par e-mail.",
  "jobs.detail.balanceReminderSendFailed": "Impossible d’envoyer le rappel",
  "jobs.detail.balanceReminderOpenFailed": "Impossible d’ouvrir le rappel",
  "jobs.detail.balanceReminderUnavailable": "Rappel de solde indisponible",
  "jobs.detail.balanceReminderUnavailableDesc":
    "Ce devis n’a plus de solde en ligne à encaisser, ou les paiements fractionnés ne sont pas configurés.",
  "jobs.detail.balanceReminderNoShareLink": "Envoyez d’abord le devis au client pour qu’il ait un lien de paiement.",
  "jobs.detail.balanceReminderNoEmail": "Ajoutez l’e-mail du client sur le devis lié avant d’envoyer un rappel.",
  "jobs.detail.balanceReminderTooShort": "Message trop court",
  "jobs.detail.balanceReminderTooShortDesc": "Ajoutez un peu plus de détail avant d’envoyer.",
  "jobs.detail.paymentShowBreakdown": "Afficher le détail",
  "jobs.detail.paymentHideBreakdown": "Masquer le détail",
  "jobs.detail.paymentBreakdownTitle": "Par devis",
  "jobs.detail.paymentSplitHint": "{{pct}} % d’acompte · solde plus tard",
  "jobs.detail.paymentDueAtEndHint": "Montant total à la fin des travaux",
  "jobs.detail.requestDepositPayment": "Demander l’acompte",
  "jobs.detail.requestFullPayment": "Demander le paiement",
  "jobs.detail.depositReminderSubjectPrefix": "Acompte dû",
  "jobs.detail.paymentReminderSubjectPrefix": "Paiement dû",
  "jobs.detail.depositReminderTitle": "E-mail client — acompte dû",
  "jobs.detail.paymentReminderTitle": "E-mail client — paiement dû",
  "jobs.detail.openJob": "Ouvrir le job",
  "projects.detail.paymentTitle": "Paiements (tous les jobs du projet)",
  "settings.title": "Paramètres",
  "settings.subtitle": "Profil entreprise et ce que les clients voient sur les devis.",
  "settings.language": "Langue",
  "settings.languageDescription":
    "Menus, boutons et texte des devis générés par IA (titre, périmètre, lignes).",
  "settings.saveHint": "Les modifications ne sont enregistrées que lorsque vous cliquez sur Enregistrer.",
  "settings.saved": "Enregistré",
  "settings.save": "Enregistrer",
  "settings.profileUpdated": "Votre profil a été mis à jour.",
  "settings.saveProfile": "Enregistrer le profil",
  "settings.saveProfileHint":
    "Enregistre la langue, les coordonnées et l’image de marque. La langue met à jour les libellés dans toute l’app.",
  "settings.saving": "Enregistrement…",
  "settings.saveChanges": "Enregistrer tous les paramètres",
  "settings.unsavedChanges": "Des modifications du profil ne sont pas enregistrées.",
  "settings.offlineBanner":
    "Vous êtes hors ligne. Les modifications du profil sont enregistrées sur cet appareil et se synchronisent à la reconnexion.",
  "settings.autosaveHint": "Les modifications du profil s’enregistrent automatiquement.",
  "settings.offlineSavedLocal":
    "Enregistré sur cet appareil. Elles seront synchronisées avec votre compte une fois en ligne.",
  "settings.offlineQueuedNetwork":
    "Impossible d’atteindre le serveur. Vos modifications sont sur cet appareil et seront réessayées quand la connexion s’améliorera.",
  "settings.onlineSynced": "Les modifications hors ligne ont été synchronisées avec votre compte.",
  "settings.offlineLocalStorageFailed":
    "Impossible d’enregistrer une copie locale. Vérifiez les autorisations de stockage du navigateur ou l’espace disque.",
  "settings.leaveUnsaved":
    "Des modifications du profil ne sont pas enregistrées. Quitter cette page sans enregistrer ?",
  "settings.partialSaveMigrationHint":
    "Les autres champs ont été enregistrés. Si le téléphone personnel ou l’e-mail de contact ne s’enregistrent pas, exécutez la dernière migration de base de données pour votre projet.",
  "signup.title": "Créer un compte",
  "signup.language": "Langue",
  "signup.languageHint":
    "Utilisé pour l’app et les devis rédigés par IA. Modifiable quand vous voulez dans Paramètres.",
  "signup.description": "Créez votre compte avec un mot de passe, puis confirmez depuis votre e-mail pour terminer.",
  "signup.requiredBefore": "Les champs marqués d’un",
  "signup.requiredAfter": "sont obligatoires.",
  "signup.fieldOptional": "(facultatif)",
  "signup.email": "E-mail",
  "signup.firstName": "Prénom",
  "signup.lastName": "Nom",
  "signup.companyName": "Nom de l’entreprise",
  "signup.trade": "Métier",
  "signup.selectTrade": "Sélectionner un métier",
  "signup.tradeCustom": "Votre métier ou spécialité",
  "signup.city": "Ville",
  "signup.state": "État",
  "signup.password": "Mot de passe",
  "signup.confirmPassword": "Confirmer le mot de passe",
  "signup.createAccount": "Créer un compte",
  "signup.creatingAccount": "Création du compte…",
  "signup.alreadyHaveAccount": "Vous avez déjà un compte ?",
  "signup.signIn": "Se connecter",
  "signup.consentBefore": "J’accepte la",
  "signup.termsOfService": "Conditions d’utilisation",
  "signup.privacyPolicy": "Politique de confidentialité",
  "signup.trades.hvac": "CVC (HVAC)",
  "signup.trades.plumbing": "Plomberie",
  "signup.trades.electrical": "Électricité",
  "signup.trades.roofing": "Toiture",
  "signup.trades.landscaping": "Aménagement paysager",
  "signup.trades.painting": "Peinture",
  "signup.trades.general": "Entrepreneur général",
  "signup.trades.other": "Autre",
  "public.language": "Langue",
  "public.languageSaved": "Préférence de langue enregistrée.",
  "public.professionalEstimate": "Devis",
  "public.threadTitle": "Écrire à votre entrepreneur",
  "public.threadSubtitle":
    "Le même fil que le bureau de votre artisan. Calendrier, périmètre, suivi.",
  "public.threadEmpty":
    "Pas encore de messages. Posez une question ou confirmez les détails — ils répondront ici.",
  "public.threadYourMessage": "Votre message",
  "public.threadPlaceholder": "ex. Pouvons-nous commencer mardi prochain ?",
  "public.threadSend": "Envoyer le message",
  "public.threadSending": "Envoi…",
  "public.threadYou": "Vous",
  "public.threadContractor": "Entrepreneur",
  "messages.you": "Vous (entrepreneur)",
  "messages.customer": "Client",
  "messages.translatedHint": "Traduit dans votre langue",
  "messages.threadTitle": "Messages avec le client",
  "messages.threadSubtitle":
    "Le même fil que le client voit sur le devis public après envoi. Les messages peuvent être traduits automatiquement.",
  "messages.noMessages":
    "Pas encore de messages. Après envoi du devis, votre première note apparaît sur leur page.",
  "messages.reply": "Répondre au client",
  "messages.send": "Envoyer au client",
  "messages.sending": "Envoi…",
  "messages.loading": "Chargement…",
  "messages.pageTitle": "Messages",
  "messages.pageSubtitle":
    "Demandes du portail propriétaires et fils de messages liés aux devis de votre entreprise.",
  "messages.loadFailedTitle": "Impossible de charger les messages",
  "messages.replyTooShort": "Saisissez d’abord une réponse.",
  "messages.replySaved": "Réponse enregistrée.",
  "messages.emailNotifyTitle": "Notifications e-mail",
  "messages.emailNotifyDescription":
    "Choisissez si nous vous prévenons par e-mail chaque fois que vous recevez un message.",
  "messages.emailNotifyCheckbox":
    "M’envoyer un e-mail dès qu’un client m’écrit un nouveau message (utilise l’e-mail de contact des devis).",
  "messages.newMessage": "Nouveau message",
  "messages.groupCustomers": "Clients",
  "messages.groupCustomersHint": "Messages depuis le portail et réponses sur les devis envoyés",
  "messages.groupTeam": "Votre équipe",
  "messages.groupTeamHint": "Équipe terrain, sous-traitants et co-opérateurs",
  "messages.sectionTeamThreads": "Messages équipe et co-opérateur",
  "messages.emptyTeamThreads":
    "Pas encore de conversations d’équipe. Démarrez un nouveau message ci-dessus pour écrire à quelqu’un de votre équipe.",
  "messages.sectionPortalContact": "Demandes de contact du portail propriétaires",
  "messages.emptyPortalContact": "Pas encore de messages du portail.",
  "messages.sectionQuoteThreads": "Fils de messages sur les devis",
  "messages.emptyQuoteThreads":
    "Pas encore de fils. Quand les clients écrivent depuis un devis envoyé, le fil complet apparaît ici.",
  "messages.replied": "Répondu",
  "messages.needsReply": "Réponse attendue",
  "messages.waitingOnCustomer": "En attente du client",
  "messages.yourReply": "Votre réponse",
  "messages.sendReply": "Envoyer la réponse",
  "messages.untitledQuote": "Devis",
  "messages.openQuote": "Ouvrir le devis",
  "messages.messageCount": "messages",
  "messages.customerLink": "Lien côté client",
  "messages.youContractor": "Vous",
  "messages.emailNotifyTapHint": "Appuyez pour modifier",
  "messages.emailNotifyCloseHint": "Appuyez pour réduire",
  "messages.portalThreadSubtitle": "Portail propriétaires",
  "messages.composerPlaceholder": "Message…",
  "messages.composerHint": "Entrée pour envoyer · Maj+Entrée pour une nouvelle ligne",
  "messages.quoteThreadFooter": "Envoyez de nouveaux messages depuis le devis ; le fil se met à jour ici.",
  "messages.tabAll": "Tous",
  "messages.tabPortal": "Portail",
  "messages.tabQuotes": "Devis",
  "messages.tabTeam": "Équipe",
  "messages.sourcePortal": "Portail",
  "messages.sourceQuote": "Devis",
  "messages.sourceTeam": "Équipe",
  "messages.selectConversation": "Sélectionnez une conversation",
  "messages.selectConversationHint": "Choisissez un fil à gauche pour lire et répondre.",
  "messages.emptyInbox": "Pas encore de conversations dans cet onglet.",
  "messages.backToList": "Tous les messages",

  "common.error": "Erreur",
  "common.copied": "Copié",

  "settings.stripe.notEnabled":
    "Stripe Connect n’est pas encore activé sur ce compte. Activez-le dans Stripe, puis reliez le compte de versement.",
  "settings.stripe.migration":
    "Impossible d’enregistrer le champ de versement. Exécutez la dernière migration de base de données, puis réessayez.",
  "settings.stripe.unknown": "Un problème est survenu au démarrage de la configuration des versements. Réessayez.",
  "settings.stripe.success":
    "La configuration des versements Stripe s’est ouverte correctement. Si vous avez terminé l’intégration, le compte sera prêt une fois la vérification Stripe terminée.",

  "settings.plan.title": "Forfait et facturation",
  "settings.plan.currentPlan": "Forfait actuel",
  "settings.plan.planStatus": "Statut du forfait",
  "settings.plan.noPlan": "Aucun forfait",
  "settings.plan.sentQuotesUsed": "{{used}} sur {{limit}} devis envoyés ce mois-ci.",
  "settings.plan.sentQuotesUsedUnlimited": "{{used}} devis envoyés ce mois-ci — illimités sur tous les forfaits.",
  "settings.plan.monthlySentQuotesUsage": "Devis envoyés ce mois-ci",
  "settings.plan.sentQuotesDraftNote":
    "Tous les forfaits incluent des envois illimités — e-mail, export PDF et copie du lien partagent le même flux. L’aperçu et les brouillons ne comptent jamais.",
  "settings.plan.startPlan": "Souscrivez un forfait pour créer des devis.",
  "settings.plan.trialPrices":
    "Tous les forfaits commencent par un essai gratuit de 7 jours — sans carte. Après le 7ᵉ jour, ajoutez une carte et choisissez un forfait pour continuer à créer des devis.",
  "settings.plan.compareIntro": "Comparez les fonctionnalités, les outils IA et l’accès équipe. Tous les forfaits incluent des envois illimités.",
  "settings.plan.ownerPromoTrialCallout":
    "Essai avec code promo : environ {{days}} jour(s) civils restants sur votre forfait offert. Ajoutez une carte sous Facturation avant la fin pour conserver l’accès.",
  "settings.plan.trialPanelLabel": "Essais et tarifs d’introduction",
  "settings.plan.aiFeaturesHasPlan": "Comparer les forfaits et fonctionnalités",
  "settings.plan.aiFeaturesNoPlan": "Comparer les forfaits et fonctionnalités",
  "settings.plan.bulletSolo":
    "Solo : devis envoyés illimités, propriétaire, portail client et flux principal, messagerie intégrée, envoi planifié, relance en 1 étape",
  "settings.plan.bulletPro":
    "Pro : devis envoyés illimités, rédaction IA, assistant devis, revue IA, saisie vocale/photo, 2 collègues (extras à 15 $/siège), 10 calendrier en lecture seule, relance en 2 étapes, journal d’activité",
  "settings.plan.bulletAgency":
    "Agency : devis envoyés illimités, toutes les fonctions IA, 10 collègues (extras à 15 $/siège, sans plafond), équipe illimitée, support prioritaire, 3 étapes + AI quote coach, marque blanche",
  "settings.plan.table.rowPrioritySupport": "Priority support",
  "settings.plan.table.headFeature": "Fonctionnalité",
  "settings.plan.table.headStarter": "Starter",
  "settings.plan.table.headSolo": "Solo",
  "settings.plan.table.headPro": "Pro",
  "settings.plan.table.headAgency": "Agency",
  "settings.plan.table.rowQuotes": "Devis envoyés (plafond mensuel)",
  "settings.plan.table.rowTeam": "Connexions collègues (accès complet)",
  "settings.plan.table.teamSeatsUpTo": "Jusqu’à {{n}}",
  "settings.plan.table.rowCalendar": "Calendrier d’entreprise",
  "settings.plan.table.rowCustomerPortal": "Portail client",
  "settings.plan.table.rowDeposits": "Paiements fractionnés / acomptes",
  "settings.plan.table.rowTeamActivityAudit": "Journal d’activité équipe (qui a modifié quoi)",
  "settings.plan.table.teamAuditCellSolo": "Pro et Agency",
  "settings.plan.table.followUp1": "1 étape",
  "settings.plan.table.followUp2": "2 étapes",
  "settings.plan.table.followUp3": "3 étapes",
  "settings.plan.table.followUpCellSolo": "Relances en 1 étape",
  "settings.plan.table.followUpCellPro": "2 étapes + suivi et analyses",
  "settings.plan.table.followUpCellAgency": "3 étapes + analyses + coach IA",
  "settings.plan.table.valueSoloOwnerWorkspace": "Espace propriétaire et calendrier bureau",
  "settings.plan.table.valueSoloNewQuoteAiOnly": "IA à la création du devis",
  "settings.plan.table.valueJorvioPortalExperience": "Portail et devis sous marque Jorvio",
  "settings.plan.table.valueAgencyWhiteLabel": "Votre marque sur portail et devis",
  "settings.plan.table.valueOfficeCalendarJobs": "Calendrier bureau et chantiers",
  "settings.plan.table.valueStandardSupport": "Support standard",
  "settings.plan.table.valuePrioritySupport": "Support prioritaire",
  "settings.plan.table.rowCrew": "Calendrier équipe (lecture seule)",
  "settings.plan.table.rowHelper": "Assistant devis",
  "settings.plan.table.rowAiDrafting": "Rédaction de devis par IA",
  "settings.plan.table.rowVoicePhoto": "Saisie vocale et photo",
  "settings.plan.table.rowAiReview": "Revue IA des devis",
  "settings.plan.table.rowMaterialLookup": "Recherche de prix matériaux en direct",
  "settings.plan.table.rowFollowUps": "Relance automatisée et analyses",
  "settings.plan.table.rowWhiteLabel": "Image côté client",
  "settings.plan.table.valuePriorityIncluded": "Inclus",
  "settings.plan.table.valuePriorityNotIncluded": "—",
  "settings.plan.table.valueOwnerOnly": "Propriétaire uniquement",
  "settings.plan.table.valueNotIncluded": "—",
  "settings.plan.table.valueIncluded": "Inclus",
  "settings.plan.table.valueUnlimited": "Illimité",
  "settings.plan.table.crewUpTo10": "Jusqu’à 10",
  "settings.plan.depositLine": "Paiements fractionnés / acomptes : {{status}}",
  "settings.plan.included": "Inclus",
  "settings.plan.upgradeProAgency": "Passez à Pro ou Agency",
  "settings.plan.ownerBypass": "Contour facturation propriétaire actif pour ce compte.",
  "settings.plan.managePlans": "Gérer le forfait et la facturation",
  "settings.plan.manageSubscription": "Paiement et factures",
  "settings.plan.viewPlans": "Voir les forfaits",
  "settings.plan.changePlan": "Changer de forfait",
  "settings.plan.nextChargeLabel": "Prochain prélèvement",
  "settings.plan.nextChargeOwnerBypass": "Compte propriétaire — facturation contournée pour les tests.",
  "settings.plan.nextChargeNoPlan": "Aucun prélèvement à venir — choisissez un forfait pour créer des devis.",
  "settings.plan.nextChargeTrialHeadline": "Premier prélèvement après l’essai le {{date}}.",
  "settings.plan.nextChargeRenewalHeadline": "Renouvellement le {{date}}.",
  "settings.plan.nextChargeDateOnly": "Prochaine date de facturation : {{date}}.",
  "settings.plan.nextChargeUnknown": "La date apparaîtra une fois l’abonnement actif.",
  "settings.plan.billingIntervalAnnual": "/an",
  "settings.plan.billingIntervalMonthly": "/mois",
  "settings.plan.billingCycleMonthly": "Facturation mensuelle",
  "settings.plan.billingCycleAnnual": "Facturation annuelle",
  "settings.plan.ownerManagesBilling": "Le propriétaire du compte gère la facturation.",
  "settings.plan.ownerManagesBillingShort": "Facturation gérée par le propriétaire",
  "settings.plan.scheduledChange": "Changement prévu : passage à {{tier}} le {{date}}.",
  "settings.plan.statusTrialing": "Essai gratuit",
  "settings.plan.statusActive": "Actif",
  "settings.plan.statusPastDue": "En retard",
  "settings.plan.statusCanceled": "Annulé",
  "settings.plan.statusInactive": "Inactif",
  "settings.plan.unlimited": "Illimité",
  "settings.plan.shortDesc.starter":
    "Pour démarrer : devis envoyés illimités et le flux essentiel pour un seul propriétaire.",
  "settings.plan.shortDesc.solo":
    "Pour un propriétaire seul : devis envoyés illimités (e-mail, PDF ou lien) et le flux complet devis et chantiers.",
  "settings.plan.shortDesc.pro":
    "Pour les petites équipes : rédaction IA, assistant devis, revue IA, saisie vocale/photo, calendrier équipe, analyses et envois illimités.",
  "settings.plan.shortDesc.agency":
    "Pour les grosses équipes : envois illimités (e-mail, PDF ou lien), votre marque sur le portail, support prioritaire et coach IA.",

  "settings.payout.title": "Compte de versement",
  "settings.payout.description":
    "Reliez le compte qui reçoit les paiements par carte en ligne sur les checkouts de devis. Vous pouvez envoyer des devis par e-mail ou PDF sans cela—connectez-le quand vous voulez le paiement par carte sur le lien du devis.",
  "settings.payout.connected":
    "Votre versement est relié via Stripe. Une fois la vérification terminée, les paiements par carte sur le lien peuvent y être versés.",
  "settings.payout.notConnected":
    "Pas encore relié—ce n’est pas bloquant. Vous pouvez envoyer des devis par e-mail ou PDF. Reliez ce compte quand vous voulez le paiement par carte sur la page publique.",
  "settings.payout.verified": "Vérifié et prêt. Les paiements par carte des devis seront versés directement sur votre compte bancaire.",
  "settings.payout.pending": "La configuration des paiements n'est pas encore terminée. Terminez la configuration pour commencer à accepter les paiements par carte sur les liens de devis.",
  "settings.payout.finishSetup": "Terminer la configuration",
  "settings.payout.finishSetupHint": "Finalisez la vérification et ajoutez votre compte bancaire dans Stripe",
  "settings.payout.verifying": "Stripe vérifie vos informations",
  "settings.payout.verifyingHint":
    "Cela prend généralement quelques minutes, mais peut aller jusqu'à 1 à 2 jours ouvrés pour certains comptes (le plus souvent lorsqu'un compte bancaire est ajouté par micro‑dépôt). Rien à faire de votre côté — nous vous enverrons un e‑mail dès que les paiements par carte seront actifs.",
  "settings.payout.connect": "Connecter le compte de versement",
  "settings.payout.connectHint": "Configurez votre compte bancaire pour recevoir les paiements par carte sur les liens de devis",
  "settings.payout.updateSetup": "Modifier la configuration des versements",
  "settings.payout.updateSetupHint": "Changez votre compte bancaire ou mettez à jour la vérification dans Stripe",
  "settings.payout.openDashboard": "Voir les versements et le solde",
  "settings.payout.openDashboardHint": "Consultez les dépôts, l'historique et le calendrier des versements dans Stripe",
  "settings.payout.banner.verifiedTitle": "Configuration de versement terminée",
  "settings.payout.banner.verifiedBody":
    "Tout est prêt : vos clients peuvent payer par carte sur les liens de devis et les dépôts arriveront sur votre banque.",
  "settings.payout.banner.verifyingTitle": "C'est noté — Stripe vérifie vos informations",
  "settings.payout.banner.verifyingBody":
    "Habituellement quelques minutes, mais cela peut aller jusqu'à 1 à 2 jours ouvrés pour certains comptes. Nous vous enverrons un e‑mail dès que les paiements par carte seront actifs — pas besoin de revenir.",
  "settings.payout.banner.incompleteTitle": "Configuration non terminée",
  "settings.payout.banner.incompleteBody":
    "Il semble que Stripe n'ait pas reçu toutes les informations nécessaires. Cliquez sur « Terminer la configuration » ci‑dessous pour reprendre — votre progression est enregistrée.",
  "settings.payout.banner.errorTitle": "Impossible de joindre Stripe",
  "settings.payout.banner.errorBody":
    "Nous n'avons pas pu confirmer votre statut auprès de Stripe à l'instant. Cliquez sur « Vérifier le statut maintenant » dans une minute, ou rechargez la page.",
  "settings.payout.footer":
    "Les coordonnées bancaires restent chez Stripe. Jorvio Quotes ne stocke pas les RIB complets dans cette app. L’envoi par e-mail ou PDF ne nécessite pas de compte de versement.",
  "settings.payout.depositTimingNote":
    "Selon le calendrier standard de Stripe, les fonds sont versés sur votre compte bancaire environ 2 jours ouvrés après chaque paiement réussi. Le premier versement peut prendre 7 à 14 jours, le temps que Stripe vérifie le nouveau compte.",

  "settings.archive.title": "Stockage des devis",
  "settings.archive.description": "Gérez les devis archivés.",
  "settings.archive.button": "Voir les devis archivés",
  "settings.archivedPageTitle": "Devis et chantiers archivés",
  "settings.archivedPageSubtitle":
    "Les devis archivés sont masqués dans la liste principale. Les chantiers archivés sont masqués dans Actifs.",
  "settings.archivedQuotesSection": "Devis archivés",
  "settings.archivedJobsSection": "Chantiers archivés",
  "settings.archivedJobsEmpty": "Aucun chantier archivé.",
  "settings.archivedJobRestore": "Restaurer le chantier",
  "settings.archivedQuotesEmptyTitle": "Aucun devis archivé",
  "settings.archivedQuotesEmptyDesc": "Les devis archivés apparaîtront ici.",
  "settings.archivedDeleteQuoteAria": "Supprimer le devis archivé",

  "settings.followup.title": "Automatisation de relance",
  "settings.followup.description":
    "Créez une séquence de rappels par défaut après l’envoi initial d’un devis.",
  "settings.followup.agencyIncluded": "Agency inclut des relances personnalisables pour les devis envoyés.",
  "settings.followup.agencyAvailable": "Les relances personnalisées sont disponibles sur le forfait Agency.",
  "settings.followup.manage": "Gérer l’automatisation",
  "settings.followup.viewFeature": "Voir la fonction Agency",

  "settings.owner.title": "Tableau propriétaire",
  "settings.owner.description":
    "Métriques internes, analyses d’abonnement et contrôles de test pour votre compte.",
  "settings.owner.open": "Ouvrir le tableau propriétaire",

  "settings.crew.title": "Accès calendrier équipe",
  "settings.crew.description":
    "Partagez un code d’équipe pour un calendrier en lecture seule des chantiers assignés. Connexion depuis la page de login avec « Entrer le code d’équipe » — sans e-mail. Les noms apparaissent ici et dans « Assigner à » sur les devis et le calendrier.",
  "settings.crew.hintRotate":
    "Toute personne avec le code peut lancer l’inscription équipe. Créer un nouveau code invalide l’ancien pour les nouvelles inscriptions seulement ; cela ne retire pas l’accès à ceux qui utilisaient déjà un ancien code.",
  "settings.crew.noCodeYet": "Pas encore de code d’équipe. Générez-en un pour activer l’accès.",
  "settings.crew.activeCodeLabel": "Votre code d’équipe actif",
  "settings.crew.copyCode": "Copier le code",
  "settings.crew.codeHiddenTitle": "Un code est actif mais n’est pas stocké pour affichage.",
  "settings.crew.codeHiddenBody":
    "Générez un nouveau code ci-dessous pour l’afficher et le partager. Cela invalide l’ancien uniquement pour les nouvelles inscriptions ; partagez le nouveau avec les nouvelles recrues.",
  "settings.crew.generating": "Génération…",
  "settings.crew.generateNew": "Nouveau code d’équipe",
  "settings.crew.generateFirst": "Générer un code d’équipe",
  "settings.crew.refreshRoster": "Actualiser la liste",
  "settings.crew.rosterTitle": "Membres (calendrier équipe)",
  "settings.crew.rosterHint":
    "Personnes ayant ouvert le calendrier équipe au moins une fois. Utilisez ces noms pour assigner les chantiers.",
  "settings.crew.noSignins":
    "Pas encore de connexions. Partagez le code depuis la page de login ; la liste se met à jour à l’ouverture du calendrier.",
  "settings.crew.tableName": "Nom",
  "settings.crew.tableLastActive": "Dernière activité",
  "settings.crew.tableVisits": "Visites",
  "settings.crew.toastCodeFail": "Impossible de mettre à jour le code",
  "settings.crew.toastCodeUpdatedTitle": "Code d’équipe mis à jour",
  "settings.crew.toastCodeUpdatedDesc":
    "Copiez le nouveau code pour les nouvelles recrues. L’ancien code ne fonctionne plus pour les nouvelles inscriptions ; les personnes déjà dans l’équipe conservent l’accès.",

  "settings.cooperator.title": "Accès co-opérateur",
  "settings.cooperator.description":
    "Partenaire de confiance : mêmes pouvoirs sur devis et calendrier, sauf supprimer le compte entreprise. Code distinct du calendrier équipe.",
  "settings.cooperator.hint":
    "Inscription via Rejoindre avec le code après partage. Le code calendrier sert uniquement au calendrier terrain.",
  "settings.cooperator.noCodeYet": "Pas encore de code co-opérateur. Générez-en un pour inviter.",
  "settings.cooperator.activeCodeLabel": "Code d’invitation co-opérateur actif",
  "settings.cooperator.copyCode": "Copier le code",
  "settings.cooperator.codeHiddenTitle": "Un code est actif mais n’est pas stocké pour affichage.",
  "settings.cooperator.codeHiddenBody":
    "Générez un nouveau code pour l’afficher et le partager. Cela invalide l’ancienne invitation pour les nouvelles inscriptions seulement ; les personnes déjà inscrites conservent l’accès.",
  "settings.cooperator.generating": "Génération…",
  "settings.cooperator.generateNew": "Nouveau code co-opérateur",
  "settings.cooperator.generateFirst": "Générer un code co-opérateur",
  "settings.cooperator.toastCodeFail": "Impossible de mettre à jour le code",
  "settings.cooperator.toastCodeUpdatedTitle": "Code co-opérateur mis à jour",
  "settings.cooperator.toastCodeUpdatedDesc":
    "Partagez le nouveau code pour les nouveaux co-opérateurs. L’ancienne invitation ne fonctionne plus pour les nouvelles inscriptions ; les personnes déjà inscrites conservent l’accès.",

  "settings.crewInbox.title": "Messages équipe",
  "settings.crewInbox.subtitle":
    "Échangez avec les personnes connectées via votre code d’équipe. Elles voient ce fil sur leur page calendrier après avoir rejoint.",
  "settings.crewInbox.emptyList":
    "Pas encore de conversations. Quand quelqu’un utilise votre code, il apparaît ci-dessus. Vous pourrez lui écrire ici.",
  "settings.crewInbox.teamMember": "Membre d’équipe",
  "settings.crewInbox.pickPerson": "Choisir une personne",
  "settings.crewInbox.selectPrompt": "Sélectionnez quelqu’un pour voir les messages ou démarrer un fil.",
  "settings.crewInbox.loadingMsgs": "Chargement des messages…",
  "settings.crewInbox.noMsgs": "Pas encore de messages. Dites bonjour ci-dessous.",
  "settings.crewInbox.office": "Bureau",
  "settings.crewInbox.crew": "Équipe",
  "settings.crewInbox.writePlaceholder": "Écrire un message…",
  "settings.crewInbox.sending": "Envoi…",
  "settings.crewInbox.send": "Envoyer",
  "settings.crewInbox.refreshList": "Actualiser la liste",
  "settings.crewInbox.toastLoadFail": "Impossible de charger la boîte équipe",
  "settings.crewInbox.toastLoadMsgsFail": "Impossible de charger les messages",
  "settings.crewInbox.toastChooseMember": "Choisissez un membre et saisissez un message",
  "settings.crewInbox.toastSendFail": "Échec de l’envoi",
  "settings.crewInbox.toastSent": "Envoyé",

  "settings.logo.title": "Logo",
  "settings.logo.description": "Affiché sur les devis clients. Une image carrée est idéale.",
  "settings.section.personal.title": "Profil personnel",
  "settings.section.personal.description": "Langue, nom, e-mail de connexion, téléphone personnel et mot de passe.",
  "settings.section.business.title": "Coordonnées entreprise",
  "settings.section.business.description":
    "Identité, métier, marque et adresse qui figurent avec vos informations d’entreprise sur les devis clients le cas échéant.",
  "settings.profile.personalPhone": "Téléphone personnel",
  "settings.profile.personalPhoneHelp":
    "Pour vos dossiers uniquement — pas affiché sur les devis clients. L’app n’envoie pas de SMS et ne vérifie pas les numéros.",
  "settings.profile.quoteContactEmail": "E-mail de contact sur les devis",
  "settings.profile.quoteContactEmailHelp":
    "Affiché avec vos informations d’entreprise sur PDF et aperçu. Vide = e-mail de connexion.",
  "settings.profile.phoneOnQuotesHelp": "Affiché avec vos informations d’entreprise sur PDF et aperçu.",
  "settings.profile.requiredFieldMarker": "Obligatoire",
  "settings.validation.quoteEmail": "Saisissez un e-mail valide ou laissez vide.",
  "settings.validation.vanitySubdomain": "Utilisez seulement lettres, chiffres ou tirets (pas d'espaces).",
  "settings.nav.personal": "Personnel",
  "settings.nav.business": "Entreprise",
  "settings.nav.logos": "Logos",
  "settings.nav.ai": "IA et textes",
  "settings.nav.integrations": "Intégrations",
  "settings.nav.plan": "Offre",
  "settings.nav.payout": "Paiements",
  "settings.nav.archive": "Archives",
  "settings.nav.priceBook": "Tarifs",
  "settings.nav.ops": "Ops",
  "settings.nav.followUp": "Relances",
  "settings.nav.owner": "Propriétaire",
  "settings.nav.crew": "Équipe",
  "settings.nav.teamCode": "Code équipe",
  "settings.nav.team": "Personnes",
  "settings.nav.legal": "Juridique",
  "settings.nav.account": "Compte",
  "settings.nav.group.profileCompany": "Profil et entreprise",
  "settings.nav.group.billingWorkflow": "Facturation et flux",
  "settings.nav.group.team": "Équipe",
  "settings.nav.group.account": "Compte",
  "settings.nav.group.owner": "Propriétaire",
  "settings.tab.personal": "Profil personnel",
  "settings.tab.company": "Infos entreprise",
  "settings.tab.reminders": "Rappels intelligents",
  "settings.tab.brand": "Logos",
  "settings.tab.aiQuotes": "IA devis",
  "settings.tab.reviews": "Avis",
  "settings.tab.plan": "Forfait et facturation",
  "settings.tab.payments": "Versements",
  "settings.tab.workspace": "Archives et outils",
  "settings.tab.access": "Codes d’accès",
  "settings.tab.people": "Personnes et liste",
  "settings.tab.account": "Compte et support",
  "settings.tab.owner": "Tableau propriétaire",

  "settings.profile.title": "Profil",
  "settings.profile.description":
    "Ce qui apparaît sur les devis clients : identité d’entreprise, marque et contact.",
  "settings.profile.yourName": "Votre nom",
  "settings.profile.companyName": "Nom de l’entreprise",
  "settings.profile.phone": "Téléphone",
  "settings.profile.trade": "Métier",
  "settings.profile.selectTrade": "Choisir un métier",
  "settings.profile.tradeCustom": "Votre métier ou spécialité",
  "settings.profile.tradeCustomPlaceholder": "ex. piscines, électroménager, bricolage",
  "settings.profile.brandColor": "Couleur de marque",
  "settings.profile.licenseNumber": "Numéro de licence",
  "settings.profile.vanityQuoteSubdomain": "Sous-domaine de lien de devis personnalisé (Agency)",
  "settings.profile.vanityQuoteSubdomainHelp":
    "Définissez un sous-domaine réservé à l'Agency pour personnaliser les liens de devis clients (ex. acmeelectric -> https://acmeelectric.jorvioquotes.com/p/<token>).",
  "settings.profile.address": "Adresse",
  "settings.profile.city": "Ville",
  "settings.profile.state": "État / région",
  "settings.profile.zip": "Code postal",
  "settings.profile.emailLabel": "E-mail",
  "settings.profile.emailCurrent": "E-mail actuel du compte :",
  "settings.profile.emailUpdate": "Mettre à jour l’e-mail",
  "settings.profile.emailSending": "Envoi…",
  "settings.profile.emailHelp": "Votre nouvel e-mail devient actif seulement après que vous cliquiez sur le lien de confirmation que nous lui enverrons.",
  "settings.profile.emailEnter": "Saisissez un e-mail",
  "settings.profile.emailUpdateFailed": "Impossible de mettre à jour l’e-mail",
  "settings.profile.emailConfirmationSentTitle": "E-mail de confirmation envoyé",
  "settings.profile.emailConfirmationSentDesc": "Cliquez sur le lien envoyé à {{email}} pour changer l’e-mail de votre compte.",
  "settings.profile.emailPendingTitle": "E-mail de confirmation envoyé à {{email}}.",
  "settings.profile.emailPendingBody": "Cliquez sur le lien dans cette boîte pour changer l’e-mail de votre compte. Votre e-mail actuel reste actif jusque-là.",
  "settings.profile.emailPendingResend": "Renvoyer",
  "settings.profile.emailPendingDismiss": "Fermer",

  "settings.trade.hvac": "CVC",
  "settings.trade.plumbing": "Plomberie",
  "settings.trade.electrical": "Électricité",
  "settings.trade.roofing": "Toiture",
  "settings.trade.landscaping": "Paysagisme",
  "settings.trade.painting": "Peinture",
  "settings.trade.general": "Entrepreneur général",
  "settings.trade.other": "Autre",

  "settings.business.title": "Instructions et valeurs par défaut de l’IA",
  "settings.business.description":
    "Un seul endroit pour indiquer à l’IA comment rédiger vos devis et quelles informations standard y inclure.",
  "settings.business.reuseLabel": "Texte à réutiliser sur les devis",
  "settings.business.reusePlaceholder":
    "ex. « Frais de déplacement : 89 $, offerts si travaux acceptés. » Acompte 50 % au-delà de 1 000 $.",
  "settings.business.reuseHelp":
    "Facultatif. Enregistré ici pour éviter de le retaper. Ajoutez-le à un devis et il apparaît sur le devis que voit votre client.",
  "settings.business.aiLabel": "Instructions et informations standard pour l’IA",
  "settings.business.aiPlaceholder":
    "ex. « Toujours 40 % d’acompte d’avance. Mentionner notre garantie d’un an. Frais de déplacement 89 $, offerts si travaux acceptés. »",
  "settings.business.aiHelp":
    "Facultatif. Une seule zone pour les deux : règles de rédaction des devis par l’IA (ton, mise en forme) et informations standard à y inclure (% d’acompte, frais, garantie, politiques). L’IA suit les règles et ajoute les éléments visibles par le client sur chaque devis.",

  "settings.validation.tradeCustom": "Décrivez votre métier ou spécialité",

  "quotes.title": "Devis",
  "quotes.subtitle": "Tous les devis",
  "quotes.searchPlaceholder": "Rechercher dans le titre ou le résumé…",
  "quotes.searchAria": "Rechercher des devis",
  "quotes.sort.placeholder": "Trier par",
  "quotes.sortAria": "Trier les devis",
  "quotes.sort.updated_desc": "Dernière modification",
  "quotes.sort.created_desc": "Création récente",
  "quotes.sort.sent_desc": "Envoi récent",
  "quotes.sort.total_desc": "Total le plus élevé",
  "quotes.sort.title_asc": "Titre A–Z",
  "quotes.statusFilterPlaceholder": "Statut",
  "quotes.statusFilterAria": "Filtrer par statut",
  "quotes.status.all": "Tous les statuts",
  "quotes.status.draft": "Brouillon",
  "quotes.status.sent": "Envoyé",
  "quotes.status.viewed": "Consulté",
  "quotes.status.viewedStalled": "Consulté, sans décision (4+ jours)",
  "quotes.status.accepted": "Accepté",
  "quotes.status.declined": "Refusé",
  "quotes.status.expired": "Expiré",
  "quotes.paymentBadge.paid": "Payé",
  "quotes.paymentBadge.depositPaid": "Acompte reçu",
  "quotes.paymentBadge.refunded": "Remboursé",
  "quotes.newQuote": "Nouveau devis",
  "quotes.loadErrorTitle": "Impossible de charger les devis",
  "quotes.loadErrorDesc": "Échec de la requête",
  "quotes.emptyTitle": "Pas encore de devis",
  "quotes.emptyDesc":
    "Créez un devis avec des lignes assistées par IA, puis envoyez un lien ouvrable sur n’importe quel appareil.",
  "quotes.addCustomerFirst": "Ajouter un client d’abord",
  "quotes.noMatchTitle": "Aucun devis correspondant",
  "quotes.noMatchDesc": "Essayez une autre recherche, un statut, une plage de dates ou un tri.",
  "quotes.clearFilters": "Effacer les filtres",
  "quotes.loadMore": "Charger plus",
  "quotes.loadingMore": "Chargement…",
  "quotes.shownOfTotal": "{shown} sur {total} affichés",
  "quotes.dateUpdatedFrom": "Modifié depuis",
  "quotes.dateUpdatedTo": "Modifié jusqu’au",
  "quotes.dateRangeHint": "Filtrer par dernière modification (fuseau local).",
  "quotes.priceBookSearchPlaceholder": "Rechercher dans le catalogue…",
  "quotes.card.customer": "Client",
  "quotes.card.validUntil": "Valide jusqu’au",
  "quotes.card.total": "Total",
  "quotes.card.updated": "Mis à jour",
  "quotes.card.archiveTitle": "Archiver le devis",
  "quotes.card.unarchiveTitle": "Restaurer le devis",
  "quotes.card.unarchive": "Restaurer",
  "quotes.card.copyLink": "Copier le lien",
  "quotes.card.quickNote": "Note rapide",
  "quotes.card.quickNoteTitle": "Note sur la liste",
  "quotes.card.quickNotePlaceholder": "Une ligne pour votre équipe (pas visible par le client)",
  "quotes.card.quickNoteSave": "Enregistrer",
  "quotes.card.quickNoteSaved": "Note enregistrée",
  "quotes.card.quickNoteAddTitle": "Ajouter une note rapide",
  "quotes.card.quickNoteDialogTitle": "Ajouter une note rapide",
  "quotes.card.expiredBadge": "Expiré",
  "quotes.card.expiresTodayBadge": "Expire aujourd’hui",
  "quotes.card.expiresInDaysBadge": "Expire dans {{days}} jours",
  "quotes.card.expiresInOneDayBadge": "Expire dans 1 jour",
  "quotes.card.notOpenedYet": "Pas encore ouvert (aucune vue)",
  "quotes.card.movedToJobs": "Déplacé vers Jobs",
  "quotes.card.movedToJobsTitle": "Ce devis a été accepté et est maintenant suivi dans Jobs",
  "quotes.card.movedToJobsNote": "Accepté… il apparaîtra maintenant dans Jobs.",
  "quotes.card.openedTimes": "Ouvert {{count}}×",
  "quotes.card.openedLast": "Dernière {{date}}",
  "quotes.pricing.standardLabel": "Prix sur chaque ligne",
  "quotes.pricing.marginTargetLabel": "Coût + marge %",
  "quotes.pricing.standardTooltip":
    "Le prix de chaque ligne est ce que le client paie. L’assistant peut suggérer des tarifs typiques pour votre métier.",
  "quotes.pricing.marginTargetTooltip":
    "Saisissez votre coût sur chaque ligne. Le total client ajoute votre pourcentage de marge.",
  "quotes.toast.archiveFailed": "Échec de l’archivage",
  "quotes.toast.archived": "Devis archivé",
  "quotes.toast.undo": "Annuler",
  "quotes.toast.undoArchiveAlt": "Remettre ce devis dans la liste active",
  "quotes.toast.unarchiveFailed": "Échec de la restauration",
  "quotes.toast.restored": "Devis restauré",

  "dashboard.subtitle": "Aperçu des devis, du pipeline et des encaissements",
  "dashboard.scheduleNudgeTitle": "Fin de fenêtre chantier proche",
  "dashboard.scheduleNudgeIntro":
    "Ces chantiers acceptés se terminent sur place dans environ deux jours (ou tout juste). Mettez le planning à jour sur le devis si vous avez besoin de plus de temps, ou rappelez tout solde au client.",
  "dashboard.scheduleNudgeEnd": "Fin prévue",
  "dashboard.scheduleNudgeBalance": "Solde restant (estimation)",
  "dashboard.scheduleNudgeOpenQuote": "Ouvrir le devis",
  "dashboard.scheduleNudgeBalanceEmail": "E-mail solde",
  "dashboard.recentQuotes": "Devis récents",
  "dashboard.recent.empty": "Pas encore de devis.",
  "dashboard.recent.createFirst": "Créer votre premier devis",
  "dashboard.recent.viewAll": "Tout voir",
  "dashboard.recent.colQuote": "Devis",
  "dashboard.recent.colCustomer": "Client",
  "dashboard.recent.colStatus": "Statut",
  "dashboard.recent.colAmount": "Montant",
  "dashboard.recent.colUpdated": "Mis à jour",
  "dashboard.stats.summary": "{{total}} devis · {{accepted}} acceptés · {{winRate}} taux de réussite",
  "dashboard.stats.totalQuotes": "Total des devis",
  "dashboard.stats.totalQuotesHint": "Devis actifs (hors archives).",
  "dashboard.stats.quotesPending": "Devis en cours",
  "dashboard.stats.quotesPendingHint":
    "Envoyé ou ouvert : pas encore accepté, refusé ou expiré. Brouillons exclus.",
  "dashboard.stats.totalProfit": "Profit total",
  "dashboard.stats.totalProfitHint":
    "Ce que le client paie sur les devis acceptés, moins vos coûts. Inclut les chantiers acceptés même si vous avez été payé en dehors de l’app.",
  "dashboard.stats.totalProfitHintStarter":
    "Ce que le client paie sur les devis acceptés, moins les coûts que vous avez saisis sur ces devis.",
  "dashboard.stats.jobsCompleted": "Chantiers terminés",
  "dashboard.stats.jobsCompletedHint": "Chantiers marqués terminés dans Chantiers.",
  "dashboard.stats.quoteWinRate": "Taux de réussite",
  "dashboard.stats.quoteWinRateHint": "Devis acceptés divisés par tous les devis que vous avez envoyés.",
  "dashboard.stats.profitLast30": "Profit des devis acceptés (30 derniers jours)",
  "dashboard.stats.profitLast30Hint":
    "Profit le jour où chaque devis a été accepté. Même calcul que le profit total, y compris les chantiers payés en dehors de l’app.",
  "dashboard.stats.profitLast30HintStarter":
    "Profit le jour où chaque devis a été accepté. Même calcul que le profit total : total moins vos coûts.",
  "dashboard.stats.profitChartEmptyTitle": "Pas encore de devis acceptés",
  "dashboard.stats.profitChartEmptyBody":
    "Le profit quotidien s’affiche quand les clients acceptent. Basé sur le total moins vos coûts internes.",
  "dashboard.stats.profitChartOlderTitle": "Aucun devis accepté au cours des 30 derniers jours",
  "dashboard.stats.profitChartOlderBody":
    "Votre profit total ci-dessus inclut d’anciens devis acceptés. Ce graphique ne couvre que les 30 derniers jours, il reste donc plat jusqu’à ce qu’un nouveau devis soit accepté.",
  "dashboard.stats.showHint": "Afficher l’explication",
  "dashboard.stats.hideHint": "Masquer l’explication",
  "dashboard.onboarding.welcomeTitle": "Bienvenue — créons votre premier devis",
  "dashboard.onboarding.welcomeDesc":
    "Dès que vous envoyez des devis, les stats ici (envoyés, consultés, gains, devis ouverts) se mettent à jour.",
  "dashboard.onboarding.step1Title": "Ajouter un client",
  "dashboard.onboarding.step1Body": "Centralisez les coordonnées pour les chantiers récurrents.",
  "dashboard.onboarding.step2Title": "Créer votre premier devis",
  "dashboard.onboarding.step2Body": "Générez des lignes avec l’IA, puis modifiez et envoyez.",
  "dashboard.onboarding.step3Title": "Envoyer le lien",
  "dashboard.onboarding.step3Body": "Les clients ouvrent le devis sur mobile et peuvent accepter avec une signature.",
  "dashboard.onboarding.go": "Aller →",
  "dashboard.onboarding.customers": "Clients",
  "dashboard.onboarding.activityTitle": "Aperçu de l’activité",
  "dashboard.onboarding.activityDesc": "Après l’envoi de devis, des tendances s’affichent ici.",
  "dashboard.onboarding.bullet1": "Comptes envoyés et consultés par mois",
  "dashboard.onboarding.bullet2": "Totaux des devis ouverts et revenus gagnés sur devis acceptés",
  "dashboard.team.title": "Calendrier équipe",
  "dashboard.team.description":
    "Sur les devis ou chantiers, utilisez Assigner à pour indiquer qui fait le travail. Chaque personne ne voit sur le calendrier équipe (/crew) que les missions qui lui sont assignées, après connexion avec le code d’équipe.",
  "dashboard.team.calendarCode": "Voir le code d’équipe",
  "dashboard.team.crewAccessCodeHelp":
    "Ouvrez le bouton pour afficher ou copier le code. L’équipe va sur /crew, saisit le code une fois, se connecte et utilise le même nom que vous choisissez dans Assigner à.",
  "dashboard.team.codeHint":
    "Créez un code d’équipe sous Réglages → Équipe lorsque vous voulez que le personnel voie ses missions sur le calendrier.",
  "dashboard.team.sharingOn": "Calendrier équipe activé",
  "dashboard.team.since": "Code d’équipe actif depuis le {{date}}",
  "dashboard.team.teamCodeActive": "Le code d’équipe est actif.",
  "dashboard.team.signinsNone":
    "Aucune connexion équipe pour le moment. Partagez le code et demandez à chacun d’ouvrir /crew et de se connecter avec le même nom que dans Assigner à.",
  "dashboard.team.signinsOne":
    "1 personne s’est connectée sur /crew — vérifiez le nom saisi ci-dessous.",
  "dashboard.team.signinsMany":
    "{{count}} personnes se sont connectées sur /crew — vérifiez les noms saisis ci-dessous.",
  "dashboard.team.colName": "Nom saisi",
  "dashboard.team.colVisits": "Visites",
  "dashboard.team.colLastOpened": "Dernière ouverture",

  ...EXTENDED_FR,
};

export const dictionaries: Record<AppLocale, MessageDict> = {
  en,
  es,
  fr,
};

export function translateKey(locale: AppLocale, key: string): string {
  const d = dictionaries[locale] ?? dictionaries.en;
  return d[key] ?? dictionaries.en[key] ?? key;
}

/** Replace `{{name}}` placeholders in a translated string. */
export function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, k: string) => String(vars[k] ?? ""));
}
